import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  INITIAL_REPORT_STATE,
  advanceOnBreak,
  afterReport,
  eventReport,
  nextIntervalReport,
} from '../watch-progress.js';
import type { PlaybackSnapshot, ReportState } from '../watch-progress.js';
import { RESUME_PROBE_MIN_INTERVAL_MS, fetchResumePoint, shouldResync } from '../resume-return.js';
import type { ResumeCheckLog } from '../resume-return.js';
import type {
  AdBreakView,
  ChannelCatalogResponse,
  LiveView,
  PlaylistResume,
  ProgressReportReason,
  ProgressReportRequest,
} from '@apm/contracts';
import type { BloomRuntimeConfig, Tv2mLinkContext } from '@apm/contracts';
import type { AuthApi } from '../auth/use-auth.js';
import { buildOfferHandoffRequest, requestPurchaseHandoff } from '../bloom-handoff.js';
import { resolveClientType } from '../client-type.js';
import { config } from '../config.js';
import {
  HANDOFF_IDLE,
  handoffLingerMs,
  HANDOFF_SHEET,
  handoffFromError,
  handoffFromResponse,
  reasonKeyFromError,
  sheetForOffer,
  sheetFromOutcome,
  sheetLinkContext,
  sheetWithNote,
} from '../handoff.js';
import type { HandoffState, OfferSheetTarget } from '../handoff.js';
import { takeLeftForCheckout } from '../left-for-checkout.js';
import { runAdHandoff } from '../ad-handoff.js';
import { trackOfferClick } from '../offers/create-offer-adapter.js';
import { resolvePick, toProductRows } from '../product-list.js';
import { fetchCatalog } from '../catalog-client.js';
import { contentSheetMode, toContentRows } from '../content-sheet.js';
import { purchaseQrUrl } from '../qr.js';
import { healAfterClose } from '../tv-focus/use-tv-focus.js';
import { useReceiver } from '../useReceiver.js';
import type { Tv2mApi } from '../useTv2m.js';
import { ChannelSheet } from '../components/ChannelSheet.js';
import { buffersUnlocked, unlockBuffers } from '../playback-buffers.js';
import { closesSwitchEpg, neighborChannel, resolveEpgPick, watchingChannelId } from '../epg.js';
import { channelForNumber } from '../channel-keys.js';
import { useChannelNumberKeys } from '../use-channel-keys.js';
import type { EpgRow } from '../epg.js';
import { FreezeFrame } from '../components/FreezeFrame.js';
import { showsStartGate } from '../auto-start.js';
import { freezeFrameHeld } from '../freeze-frame.js';
import { ControlBar } from '../components/ControlBar.js';
import { DebugPanel } from '../components/DebugPanel.js';
import { HandoffErrorOverlay } from '../components/HandoffErrorOverlay.js';
import { InfoOverlay } from '../components/InfoOverlay.js';
import { MuteButton } from '../components/MuteButton.js';
import { OfferCard } from '../components/OfferCard.js';
import { AdCountdown, PlaybackClock } from '../components/PlaybackClock.js';
import { formatClock } from '@apm/i18n';
import { episodeLabel } from '../episode-label.js';
import { writeLastItem } from '../channels.js';
import { ProductListSheet } from '../components/ProductListSheet.js';
import { ProductSheet } from '../components/ProductSheet.js';
import { PushSentOverlay } from '../components/PushSentOverlay.js';
import { QrOverlay } from '../components/QrOverlay.js';
import { SettingsSheet } from '../components/SettingsSheet.js';
import { StartGate } from '../components/StartGate.js';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * 시청 화면. 전체화면 비디오 위에 오버레이 레이어 하나다
 * (`docs/06-frontend-spec.md` 「화면 구조」).
 *
 * **B7 전에는 이것이 앱 전체였다** (`packages/receiver-web/src/App.tsx`). 통합 뒤로는
 * `/` 경로의 화면이고, 흡수된 모바일 웹의 홈 — 기기 등록 상태와 알림 켜기 — 이
 * 설정 시트로 이 위에 겹친다. **없애면 안 된다**: Tier 2 가 성립하려면 등록과 구독이
 * 그 전에 끝나 있어야 한다 (`docs/07` §4 시나리오 B).
 */
export function WatchScreen({
  runtime,
  channelId,
  autoStart,
  tv2m,
  auth,
  lastContext,
  onOpenLast,
  epgRows,
  onEpgOpen,
  favorites,
  onToggleFavorite,
  startAt,
  resumeAt,
  onSwitchChannel,
  epgOpen = false,
  onHome,
}: {
  /** `GET /v1/bloom/config` 가 준 설정. 모드와 두 서버 주소가 여기서 온다 */
  runtime: BloomRuntimeConfig;
  /**
   * 지금 트는 채널. **`config.channelId` 를 여기서 읽지 않는다** — v9 부터 그 값은
   * 「첫 진입 시 기본으로 고를 채널」이고, 실제로 무엇을 트는지는 판정이 정한다
   * (`channel-selection.ts`). 이 값이 바뀌면 리시버가 통째로 다시 선다 (절대 규칙 27).
   */
  channelId: string;
  /** 채널 전환으로 다시 선 화면이다 — 진입 게이트를 다시 묻지 않는다 (v9, C8) */
  autoStart: boolean;
  tv2m: Tv2mApi;
  /**
   * 로그인 상태와 그 조작. **설정 시트가 로그인 입구를 겸하므로 통째로 넘긴다**
   * (`docs/21` W10) — 익명일 때 그 시트가 유일한 입구다.
   */
  auth: AuthApi;
  /** 이 기기가 마지막으로 열었던 상품. 설정 시트가 이어보기 버튼을 그린다 */
  lastContext: Tv2mLinkContext | null;
  onOpenLast: () => void;
  /**
   * 공개 채널 전부. **시트가 재생 위에 열린다** — 목록 화면으로 옮기면 이 화면이
   * 내려가 리시버가 파괴되고, 그냥 둘러보려던 시청자의 재생이 끊긴다 (절대 규칙 14).
   */
  epgRows: readonly EpgRow[];
  /**
   * 편성표 시트를 여는 순간 (v27/TG15). **`epgRows` 를 다시 받아 오라는 신호다.**
   *
   * ★ 이 화면이 직접 부르지 않는 이유는 **응답의 주인이 `ChannelGate`** 이기 때문이다 —
   * 여기서 따로 받으면 같은 편성표가 두 곳에 살고, 그 둘이 갈리는 순간 **어느 쪽이
   * 화면에 그려졌는지 알 수 없다.**
   *
   * 안 부르면 재생 중인 행만 참이고 **나머지 행은 부팅 시점으로 굳는다** (절대 규칙 68 —
   * 「첫 카드는 그 채널을 켰을 때 실제로 나오는 것이다」). 2026-09-04 무대 신고가 그것이다.
   */
  onEpgOpen: () => void;
  favorites: readonly string[];
  onToggleFavorite: (channelId: string) => void;
  /** 편성표가 「이 회차부터」로 세운 경우 (v25). `useReceiver` 가 한 번 쓰고 버린다 */
  startAt: string | null;
  /**
   * v26. **기기가 기억한 시작 항목** (절대 규칙 70). 홈쇼핑 채널에서만 값이 온다 —
   * 게이트가 `entryStartFor` 로 이미 걸렀다(자물쇠 ②).
   */
  resumeAt: string | null;
  /** 채널을 바꾼다. **같은 채널이면 호출부가 무시한다** (절대 규칙 27 의 절차가 거기 있다) */
  onSwitchChannel: (channelId: string, startFromProductId?: string, fromEpg?: boolean) => void;
  /**
   * v29. **편성표에서 고른 전환으로 들어왔다** — 시트를 연 채로 시작한다
   * (2026-09-06 · `closesSwitchEpg`). 한 번 쓰고 버린다.
   */
  epgOpen?: boolean;
  /** v32 — 「홈」 버튼. `ChannelGate` 가 이 화면을 내리고 홈을 그린다 (`docs/63` §3.3 ⑥) */
  onHome: () => void;
  /**
   * 데모 완료 화면. **판매처를 새 탭으로 연 뒤에만 닿는다** (B10) — 시트의 주 버튼은
   * 이제 `[판매처로 이동]` 이고, 그것이 BLOOM 을 떠나는 유일한 순간이다 (절대 규칙 14).
   */
}) {
  const t = useT();
  /**
   * **한 번만 읽고 붙들어 둔다.** 버튼 문구와 요청 본문과 낙관적 시트 판정이 같은 값을
   * 봐야 한다 — 렌더마다 다시 읽으면 창 크기를 바꾸는 것만으로 그 셋이 갈릴 수 있고,
   * 그때 증상은 「누른 뒤에 글자가 바뀐다」다.
   */
  const [clientType] = useState(() => resolveClientType());
  // `<video>` 두 개는 어댑터가 이 컨테이너에 넣는다. ref 콜백으로 받는 이유는
  // 엘리먼트가 생긴 시점에 어댑터를 만들어야 하기 때문이다 (ref 객체는 렌더를 트리거하지 않는다).
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const {
    view,
    telemetry,
    start,
    startFrom,
    retry,
    skip,
    skipAd,
    pausePlayback,
    resumePlayback,
    seekBy,
    sendInteraction,
    selectItem,
    joinLive,
    resumeRecorded,
    resync,
    snapshot,
    adSnapshot,
    getElapsedSec,
    getAdBreakElapsedSec,
    isRecordedLocked,
    getPlayhead,
    getLiveSlots,
    dismissOffer,
    offerSnapshot,
    engineSnapshot,
  } = useReceiver(container, channelId, autoStart, auth.token, startAt, resumeAt);

  const [handoff, setHandoff] = useState<HandoffState>(HANDOFF_IDLE);
  /**
   * ★ v23 — **누른 상품의 폴백 QR 감** (`docs/47` §9.3).
   *
   * `handoff` 안에 두지 않는 이유: `handoffFromResponse` 가 응답을 **`sheet`·`push`·`qr`·
   * `error` 중 하나**로 옮기는데, QR 이 필요한 것은 그중 **둘**(`error` 와 `push` 의 폴백
   * 자리)이고 그 둘은 상품 신원을 담을 칸이 없다. `sheetForOffer` 가 시트에만 붙는 것과
   * 같은 사정이고, **여기가 그 나머지를 받는 자리**다.
   *
   * **상품이 바뀌면 함께 비운다** (아래 `itemKey` 효과) — 앞 상품의 QR 이 남으면 스캔한
   * 사람이 화면에 없는 상품을 산다.
   */
  const [offerFallback, setOfferFallback] = useState<{
    offerId: string;
    landingUrl: string;
  } | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  /*
   * v29. **전환을 건너 열린 채로 온다** (2026-09-06). 마운트에서 한 번 읽고 버린다 —
   * 그 뒤로는 이 화면의 상태이고, 새 채널이 실제로 나오면 아래 효과가 닫는다.
   */
  const [channelSheetOpen, setChannelSheetOpen] = useState(epgOpen);
  const [productSheetOpen, setProductSheetOpen] = useState(false);
  /**
   * ★ **판매처에서 돌아온 것인가** (v21/F13). 게이트 문구 하나를 가른다 —
   * 아래 `StartGate` 의 주석과 `left-for-checkout.ts`.
   *
   * **읽으면서 지워지는 값이라 마운트에서 한 번만 읽는다.** 렌더마다 읽으면 두 번째
   * 렌더에서 이미 사라져 있다.
   */
  const [resuming] = useState(takeLeftForCheckout);
  const [settingsOpen, setSettingsOpen] = useState(false);

  /**
   * 구매 버튼. **선택 뒤 여기로 포커스를 옮긴다** (절대 규칙 35).
   *
   * `querySelector('[data-testid=purchase]')` 로 찾지 않는다 — 셀렉터가 화면 밖에 생기면
   * **조용히 낡는다.** 버튼이 `ControlBar` 에 있는 한 이 ref 는 따라간다.
   */
  const purchaseRef = useRef<HTMLButtonElement | null>(null);

  /**
   * 「전체 회차」 버튼 (v16). **일반 채널에서 선택 뒤 포커스가 여기로 온다** —
   * 그 채널에는 구매 버튼이 없다 (절대 규칙 35 의 예외).
   */
  const allProductsRef = useRef<HTMLButtonElement | null>(null);

  /**
   * 이어보기 토스트 (v16). **재생이 실제로 시작한 뒤에** 띄우고 3초 뒤 사라진다.
   *
   * **버튼이 아니다.** `<button>`·`<a href>` 를 쓰면 리모컨 포커스가 3초 뒤 사라지는
   * 요소에 갇힌다 (`docs/38` §6.7).
   */
  const [resumeToast, setResumeToast] = useState<PlaylistResume | null>(null);
  /**
   * 2026-10-29 · **라이브 앞뒤 광고 중에는 같은 채널의 회차로 가지 않는다**(채널 정책 · `docs/45` §18.8). 고른 순간 이 알림이 3초 뜬다 —
   * 리시버가 무동작이라 알리지 않으면 「눌렀는데 아무 일도 없다」다. 버튼이 아닌 것은 `resumeToast` 와 같은 이유다.
   */
  const [lockToast, setLockToast] = useState(0);
  useEffect(() => {
    if (lockToast === 0) return;
    const timer = setTimeout(() => {
      setLockToast(0);
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [lockToast]);

  const isGeneral = view.channelKind === 'general';

  /*
   * ★ v39 — **카탈로그는 채널을 틀 때 한 번 받고, 시트를 열 때 다시 받는다** (`docs/81` §5.1 · 규칙 106).
   *
   * 시트를 열 때만 받으면 응답이 오는 순간 평면 목록이 2단계로 바뀌며 **포커스가 튄다** — 시트의 모양(`contentSheetMode`)이
   * 카탈로그에 달려 있기 때문이다. 일반 채널만 부른다 — 홈쇼핑에는 라이브러리가 없다(자물쇠 · 하네스가 요청 0건을 잰다).
   * 실패는 `null` 이고 시트는 지금의 평면 목록으로 서며 머리에 한 줄을 단다(조용하지 않게).
   */
  const [catalog, setCatalog] = useState<ChannelCatalogResponse | null>(null);
  const [catalogFailed, setCatalogFailed] = useState(false);
  const loadCatalog = useCallback(() => {
    void fetchCatalog(channelId).then((res) => {
      if (res === null) {
        setCatalogFailed(true);
        return;
      }
      setCatalog(res.channelId === channelId ? res : null);
      setCatalogFailed(false);
    });
  }, [channelId]);
  useEffect(() => {
    setCatalog(null);
    setCatalogFailed(false);
    if (isGeneral) loadCatalog();
  }, [isGeneral, loadCatalog]);
  /**
   * 2026-09-10 — **이력은 `receiver` 채널 둘 다 갖는다**(규칙 46 개정). 홈쇼핑도 마지막 상품과
   * 지점을 계정에 남기고, 재진입은 그 상품 처음부터다(서버의 `resumePositionFor`). 이 화면에
   * ONAIR 는 오지 않으므로 「종류가 정해졌는가」가 곧 판정이다. `isGeneral` 은 문구와 되돌아온 뒤의
   * 재동기화(`shouldResync` · 일반만)에 남는다.
   */
  const tracksProgress = view.channelKind !== null;

  /**
   * v21. **지금 광고 구간인가** — `ref` 로 두는 이유가 있다.
   *
   * 진행 보고의 두 효과(주기·`teardown`)가 이 값을 읽는데, 의존성 배열에 넣으면
   * **브레이크가 열리고 닫힐 때마다 인터벌과 리스너가 다시 걸린다.** `teardown` 리스너가
   * 재등록되는 순간에 페이지가 사라지면 그 보고가 통째로 사라진다 — 정확히 v18 이
   * `keepalive` 로 되찾은 그 10초다.
   */
  const adBreakRef = useRef<AdBreakView | null>(null);
  adBreakRef.current = view.adBreak;
  /**
   * v33. **바이패스 중인가** — `adBreakRef` 와 **같은 자리·같은 이유**다(의존성 배열에
   * 넣으면 창이 열리고 닫힐 때마다 인터벌과 `teardown` 리스너가 다시 걸리고, 재등록되는
   * 순간에 페이지가 사라지면 그 보고가 통째로 사라진다).
   */
  const liveRef = useRef<LiveView | null>(null);
  liveRef.current = view.live;

  useEffect(() => {
    // 재생이 시작하지 않았으면 띄우지 않는다 — 검은 화면 위의 「이어봅니다」는 거짓말이다.
    // 지점이 0 이면 띄우지 않는다 — 홈쇼핑은 콘텐츠만 기억하고 처음부터 튼다(「0:00부터」는 헛말).
    if (view.resume === null || view.resume.positionSec <= 0 || view.state !== 'playing') return;
    setResumeToast(view.resume);
    const timer = setTimeout(() => {
      setResumeToast(null);
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [view.resume, view.state]);

  /**
   * 진행 보고 (v16 P7, 특허 청구항 6). **무엇을 언제 보낼지는 `watch-progress.ts` 가
   * 정한다** — 이 훅은 시계를 돌리고 실어 보내기만 한다.
   *
   * 보고가 실패해도 재생은 이어진다 (절대 규칙 7·14 의 승계). `catch` 가 삼키는 것이
   * 그 약속이고, 서버도 어떤 이유로든 202 만 돌려준다.
   */
  const reportStateRef = useRef<ReportState>(INITIAL_REPORT_STATE);

  const progressSnapshot = useCallback(
    (): PlaybackSnapshot => ({
      tracksProgress,
      channelId,
      productId: view.item?.productId ?? null,
      elapsedSec: getElapsedSec(),
      durationSec: view.item?.durationSec ?? 0,
      // **비로그인이면 보내지 않는다** (v18 규칙 3) — 축이 계정 하나라 서버가 버린다.
      loggedIn: auth.token !== null,
      // **숨어 있으면 주기 보고를 보내지 않는다** (v18). 뒤에 열어 둔 탭이 지점을
      // 가져가면 손에 든 폰이 돌아와도 그 값을 받는다 — `watch-progress.ts` 의 그 주석.
      visible: document.visibilityState === 'visible',
      deviceId: view.deviceId,
      nowMs: Date.now(),
    }),
    [
      tracksProgress,
      channelId,
      view.item?.productId,
      view.item?.durationSec,
      view.deviceId,
      auth.token,
      getElapsedSec,
    ],
  );

  const sendReport = useCallback(
    (body: ProgressReportRequest | null, snap: PlaybackSnapshot) => {
      if (body === null) return;
      // **보내지 못했어도 상태를 갱신한다** — 실패를 10초마다 재시도하지 않는다.
      reportStateRef.current = afterReport(snap);
      void fetch(`${runtime.bloomServerUrl}/v1/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(auth.token === null ? {} : { Authorization: `Bearer ${auth.token}` }),
        },
        body: JSON.stringify(body),
      }).catch(() => undefined);
    },
    [runtime.bloomServerUrl, auth.token],
  );

  /**
   * 사건 보고. 건너뛰기·선택·채널 전환·화면 닫힘이 부른다.
   *
   * @param override 지금 보고 있는 것이 아니라 **다른 회차를** 보고할 때 (선택, v18).
   */
  const reportEvent = useCallback(
    (
      /**
       * v33. **`interval` 도 받는다** — 라이브 바이패스의 진입 보고가 그 사유다
       * (`eventReport` 의 ★). 주기 보고의 입구는 여전히 `nextIntervalReport` 하나다.
       */
      reason: ProgressReportReason,
      /**
       * v25 — `channelId`·`isGeneral` 까지 덮을 수 있다. **채널을 건너는 선택**에서
       * 그것이 필요하다: 그대로 보내면 「옛 채널에 새 채널의 회차」를 쓰는 요청이 나가고
       * 서버가 소속 검사에서 **`logger.warn` 만 남기고 버린다.** 응답은 202 라
       * **화면에도 콘솔에도 아무 일이 없다.**
       */
      override?: Partial<{
        productId: string;
        elapsedSec: number;
        channelId: string;
        tracksProgress: boolean;
      }>,
    ) => {
      /*
       * ★★ v27/TG16. **광고 중이면 실을 값을 간다** — `teardown` 이 이미 하는 그것을
       * 나머지 갈래에도 준다 (`docs/45` §10.1).
       *
       * `getElapsedSec()` 은 광고 구간에서 **광고의 초**다(TG14 가 EPG 앵커에 대해
       * 배운 그 사실). 프리롤 중에 채널을 바꾸면 `channel_switch` 가 **회차 위치를
       * 3초**로 실어 보냈다 — 2026-09-04 실측이다.
       *
       * **지금까지 증상이 없었던 것은 서버의 `Math.max` 때문이다**(`progress.service.ts`)
       * — 같은 회차면 큰 값이 이긴다. 그러나 그 흡수는 **회차가 같을 때만**이고,
       * `override` 로 회차를 바꿔 보내는 갈래(선택·채널 건너기)에서는 성립하지 않는다.
       *
       * 미드롤이면 **돌아갈 자리**로 덮고, 그 값이 없는 브레이크(프리롤·회차 사이)는
       * **보고할 콘텐츠 위치가 아예 없다** — 그때는 보내지 않는다. `override` 가
       * `elapsedSec` 을 직접 준 경우(선택의 0초)는 광고와 무관하므로 그대로 둔다.
       */
      const adBreak = adBreakRef.current;
      const live = liveRef.current;
      const base = { ...progressSnapshot(), ...override };
      /*
       * ★ v33. **바이패스도 같은 자리에서 같은 일을 한다** (`docs/67` §8.1 ②).
       *
       * 실을 값은 **돌아갈 자리**(`live.resumeAtSec`)이고, **`null` 이면 보내지 않는다** —
       * 이벤트 중에 채널에 들어온 시청자라 **보고할 콘텐츠 위치가 아예 없다**(0 이 아니다 ·
       * 광고의 프리롤·회차 사이가 그런 것과 같다).
       */
      if (live !== null && override?.elapsedSec === undefined) {
        if (live.resumeAtSec === null) return;
        base.elapsedSec = live.resumeAtSec;
      } else if (adBreak !== null && override?.elapsedSec === undefined) {
        if (adBreak.resumeAtSec === null) return;
        base.elapsedSec = adBreak.resumeAtSec;
      }
      const snap = base;
      sendReport(eventReport(snap, reason), snap);
    },
    [progressSnapshot, sendReport],
  );

  // 주기 보고. **`PROGRESS_REPORT_INTERVAL_MS` 보다 촘촘히 깨운다** — 10초마다 깨우면
  // 「10초가 지났는가」 판정이 늘 조금씩 밀려 실제 주기가 11~12초가 된다.
  useEffect(() => {
    if (!tracksProgress || !view.started) return;
    const timer = setInterval(() => {
      // **재동기화를 재는 동안에는 쓰지 않는다.** 탐침과 `resync()` 사이에 이 보고가
      // 끼면 서버 지점이 **내 낡은 초로 덮이고**, 곧이어 `resync()` 가 받아 오는 값이
      // 그 덮인 값이다 — 맞추려던 동작이 스스로를 되돌린다.
      if (resyncHoldRef.current) return;
      /*
       * ★ v21. **광고 중에는 진행을 보내지 않는다** (`docs/45` §10.1).
       *
       * `getElapsedSec()` 이 **광고의 초**라 그대로 보내면 회차 위치가 15초쯤으로
       * 기록된다. 서버의 `Math.max` 가 대부분 흡수해 **증상이 없고**, 그것이 이 자리를
       * 막아야 하는 이유다 — 광고 시간은 진행이 아니다.
       */
      if (adBreakRef.current !== null) return;
      /*
       * ★ v33. **바이패스 중에도 보내지 않는다** (절대 규칙 85 · `docs/67` §8.1 ①).
       *
       * `getElapsedSec()` 이 **라이브의 초**라 그대로 보내면 회차 위치가 그 값으로
       * 기록된다. 서버의 `Math.max` 가 대부분 흡수해 **증상이 없고**, 그것이 이 자리를
       * 막아야 하는 이유다 — 광고에 대해 v21 이 배운 그것과 같은 모양이고, **창이 두
       * 시간이라 어긋남이 훨씬 크다.**
       */
      if (liveRef.current !== null) return;
      const snap = progressSnapshot();
      sendReport(nextIntervalReport(reportStateRef.current, snap), snap);
    }, 2000);
    return () => {
      clearInterval(timer);
    };
  }, [tracksProgress, view.started, progressSnapshot, sendReport]);

  /**
   * ★★ v28 — **회차 사이 브레이크가 열리면 「다음 회차 0초」를 보고한다** (`docs/58` §7).
   *
   * ── 무엇을 고치는가 ─────────────────────────────────────────
   *
   * 진행이 **「끝낸 회차의 끝」에 머무는 창**이 있었다. 최대 50초다 —
   *
   * ```
   *   … 마지막 주기 보고 ──┐
   *                       ├ 최대 10초        (회차 끝까지)
   *   회차 끝 ────────────┘
   *           회차 사이 브레이크 30초       ← 주기 보고가 막혀 있다 (v21 이 옳게 막았다)
   *   다음 회차 시작 ─────┐
   *                       ├ 최대 10초        (그 회차의 첫 주기 보고까지)
   * ```
   *
   * 그 창에서 채널을 옮겼다 오면 **끝낸 회차의 끝 + 회차 사이 광고를 다시** 본다.
   * 2026-09-05 무대에서 그렇게 신고됐고 기록이 그대로 남았다 — 2회를 `complete@240` 한
   * 3초 뒤 재진입이 **2회@237**, 22초 뒤 또 **2회@239** 였다(`docs/58` §7).
   *
   * ── ★ TG16 의 가드가 못 막는 자리다 ─────────────────────────
   *
   * 저 가드(`reportEvent` 의 `adBreak.resumeAtSec === null` → 보내지 않는다)는 **옳게
   * 동작한다.** 고치는 것은 **보내는 값**이고, 여기서 문제는 **이미 저장된 값**이다.
   *
   * ── 왜 「다음 회차」를 여기서만 알 수 있는가 ─────────────────
   *
   * 브레이크 동안 리시버의 `index` 는 **아직 끝난 회차**다 — `advance` 는 브레이크가
   * **닫힐 때** 소비된다(`closeAdBreak` → `advanceContent`). 그래서 `progressSnapshot()`
   * 은 다음 회차를 가리킬 수 없고, **화면이 `view.items` 로 직접 집는다.**
   *
   * ── 셋을 지킨다 ─────────────────────────────────────────────
   *
   * ① **`item_complete`** 를 쓴다 — 계약에 **이미 있던** 값이고(「한 회차가 끝났다」)
   *   지금까지 아무도 쓰지 않았다. `select` 를 빌리면 서버가 되감기를 허용하는 갈래로
   *   들어간다(`progress.service.ts` 의 그 삼항)
   * ② **`elapsedSec: 0` 을 `override` 로 준다** — 그래야 `reportEvent` 의 광고 가드를
   *   지난다(그 가드는 「값을 안 준 경우」만 막는다)
   * ③ **회차가 하나뿐인 채널에서는 보내지 않는다** — 다음이 곧 자기 자신이라
   *   **되감기가 된다.** 서버의 `Math.max` 가 흡수하기는 하지만(같은 회차다),
   *   그 흡수에 기대면 **`reason` 처리를 바꾸는 날 조용히 되감긴다.**
   *
   * ── 남는 창 하나 ────────────────────────────────────────────
   *
   * **광고가 없는 채널**은 회차 끝에서 다음 회차의 첫 주기 보고까지 **≤10초**가 그대로
   * 남는다. 그 창을 없애려면 `itemChanged` 에서 즉시 한 번 보고해야 하고, 그것은 이
   * 수정의 범위가 아니다(`docs/58` §7 의 (c)).
   */
  const advanceReportedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tracksProgress) return;
    // 브레이크가 닫히면 표시를 지운다 — 다음 회차 끝에서 다시 한 번 보내야 한다.
    if (view.adBreak === null) {
      advanceReportedForRef.current = null;
      return;
    }
    // **판정은 순수 함수가 낸다** — 여기 `if` 로 쓰면 넷이 node 테스트 밖으로 나간다
    // (`shouldResync`·`shouldReanchor` 가 세운 그 방침).
    const nextProductId = advanceOnBreak({
      breakKind: view.adBreak.kind,
      currentProductId: view.item?.productId ?? null,
      items: view.items,
      index: view.index,
      reportedFor: advanceReportedForRef.current,
    });
    if (nextProductId === null) return;

    advanceReportedForRef.current = view.item?.productId ?? null;
    reportEvent('item_complete', { productId: nextProductId, elapsedSec: 0 });
  }, [tracksProgress, view.adBreak, view.item, view.items, view.index, reportEvent]);

  /**
   * ★★ v33 — **바이패스에 들어가는 순간 두고 온 자리를 한 번 남긴다**
   * (`docs/67` §8.1 ③ · 절대 규칙 84).
   *
   * ── 이 한 번이 무엇을 사는가 ────────────────────────────────
   *
   * 라이브가 두 시간이면 그동안 주기 보고가 **막혀 있다**(위 ①). 그 사이에 앱을 껐다
   * 켜거나 폰으로 옮기면 리시버의 메모리는 사라지고 **복귀 지점은 서버가 아는 것이 전부**다.
   * 이 보고가 없으면 그 값이 **바이패스 직전 주기 보고**(최대 10초 전)이거나, 더 나쁘게는
   * **그 회차를 막 시작한 자리**다.
   *
   * ── `reason` 을 늘리지 않는다 (Q5·D7) ───────────────────────
   *
   * `interval` 로 보낸다. 컷 지점은 **언제나 직전 보고보다 크거나 같으므로** 서버의 단조
   * 증가 규칙(`Math.max`)이 그대로 받는다 — 되감기가 필요 없다. `PROGRESS_REPORT_REASONS`
   * 를 늘리면 서버·분석·하네스가 함께 늘고 **얻는 것은 라벨 하나**다.
   *
   * ── `null → 값` 의 순간에만 (한 번) ─────────────────────────
   *
   * `view.live` 를 의존성에 두면 창 하나에 한 번만 돈다(그 객체는 창마다 고정이다).
   * **`resumeAtSec` 이 `null` 이면 보내지 않는다** — `reportEvent` 의 가드가 그 일을
   * 하므로 여기서 다시 판정하지 않는다(판정이 둘이 되면 갈린다).
   */
  useEffect(() => {
    if (!tracksProgress) return;
    if (view.live === null) return;
    reportEvent('interval');
  }, [tracksProgress, view.live, reportEvent]);

  /**
   * 화면이 숨거나 닫힐 때 (특허 청구항 6 의 「중단 이벤트」).
   *
   * **`beforeunload` 를 쓰지 않는다** — iOS Safari 와 WKWebView 에서 신뢰할 수 없고,
   * `pagehide` 가 두 곳 모두에서 뜬다.
   *
   * ── ★ `sendBeacon` 이 아니라 `fetch(keepalive)` 다 (v18) ────
   *
   * **`sendBeacon` 은 `Authorization` 을 실을 수 없다.** v16 은 그래서 이 보고가 언제나
   * 기기 축으로 갔고(계정 축이 최대 10초 뒤처지는 것이 `docs/38` §11 의 한계였다), **v18 은
   * 축이 계정 하나라 그 보고가 갈 곳 자체가 없다** — 서버가 토큰 없는 요청을 버린다.
   * 증상은 **「끄기 직전 10초가 항상 사라진다」** 하나이고 조용하다.
   *
   * `fetch(..., { keepalive: true })` 는 문서가 사라진 뒤에도 요청을 끝내면서 **헤더를
   * 실을 수 있다.** Safari 13+ 이라 iPhone 7 에서도 돈다.
   */
  useEffect(() => {
    if (!tracksProgress) return;
    const flush = () => {
      /*
       * ★ v21. **`teardown` 은 막지 않는다** — 막으면 「떠나기 직전 10초가 늘 사라진다」가
       * 되고, 그것이 v18 이 `keepalive` 로 겨우 되찾은 그 10초다.
       *
       * 대신 **실을 값을 간다.** 광고 중이면 `getElapsedSec()` 이 광고의 초이므로,
       * 미드롤이면 **돌아갈 자리**(`resumeAtSec`)로 덮어 보낸다. 그 값이 없는 브레이크
       * (프리롤·회차 사이)는 **보고할 콘텐츠 위치가 아예 없다** — 그때는 보내지 않는다.
       */
      const adBreak = adBreakRef.current;
      const live = liveRef.current;
      const base = progressSnapshot();
      /*
       * ★ v34. **바이패스도 같은 일을 한다** — 위 `reportEvent` 의 v33 가드가 **이 흐름에만
       *   없었다**(2026-09-24 · `verify-live-cue` 의 `returnsToCut` 이 찾았다). 바이패스 중
       *   `getElapsedSec()` 은 **라이브의 초**라, 그대로 보내면 회차 위치가 그 값이 되고 서버의
       *   `Math.max` 가 컷을 덮는다 — 실측: 컷 38초에 들어가 40초쯤 라이브를 보다 채널을
       *   다녀오니 **47초로** 돌아왔다. 차이가 초 단위라 화면으로는 아무도 신고하지 않는다
       *   (절대 규칙 84 가 막으려는 그 조용함). 돌아갈 자리가 없으면(이벤트 중에 들어왔다)
       *   **보내지 않는다** — 광고의 프리롤과 같다.
       */
      if (live !== null && live.resumeAtSec === null) return;
      if (live === null && adBreak !== null && adBreak.resumeAtSec === null) return;
      const returnAtSec = live !== null ? live.resumeAtSec : (adBreak?.resumeAtSec ?? null);
      const snap = returnAtSec === null ? base : { ...base, elapsedSec: returnAtSec };
      const body = eventReport(snap, 'teardown');
      if (body === null) return;
      reportStateRef.current = afterReport(snap);
      void fetch(`${runtime.bloomServerUrl}/v1/progress`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(auth.token === null ? {} : { Authorization: `Bearer ${auth.token}` }),
        },
        body: JSON.stringify(body),
      }).catch(() => undefined);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tracksProgress, progressSnapshot, runtime.bloomServerUrl, auth.token]);

  /**
   * ★ 돌아왔을 때 이어보기를 다시 맞춘다 (v18).
   *
   * **이어보기는 플레이리스트를 받을 때 적용되고**, 리시버는 채널·토큰·컨테이너가 바뀔 때만
   * 다시 선다(`useReceiver.ts`). 그래서 앱을 켜 둔 채 다른 기기를 보다가 돌아오면 **아무것도
   * 다시 묻지 않는다** — 화면은 떠날 때 그 자리 그대로다. 2026-08-19 기록의 모양이 정확히
   * 그것이고(아이폰은 하트비트 말고 아무것도 요청하지 않았다), 이 효과가 그 자리를 채운다.
   *
   * **판정은 `resume-return.ts` 의 순수 함수가 한다** — 되감기지 않는 규칙과 15초 문턱이
   * 거기 있고, 여기 두면 node 환경 테스트가 보지 못한다.
   *
   * `pageshow` 를 함께 듣는다 — iOS 가 bfcache 에서 되살릴 때 `visibilitychange` 없이
   * 그것만 오는 경우가 있다. 둘 다 와도 `resyncHoldRef` 가 겹침을 막는다.
   */
  const resyncHoldRef = useRef(false);
  const lastProbeAtRef = useRef(0);
  /**
   * 마지막 확인의 기록. **`?debug=1` 패널이 이 값을 그린다.**
   *
   * **없으면 실패를 가를 수 없다** — 「사건이 오지 않았다」와 「와서 안 움직이기로 했다」가
   * 화면에서 똑같이 **아무 일도 없음**이다.
   */
  const [resumeCheck, setResumeCheck] = useState<ResumeCheckLog | null>(null);

  useEffect(() => {
    if (!isGeneral || auth.token === null) return;

    let dropped = false;
    const onReturn = (by: ResumeCheckLog['by']) => () => {
      if (document.visibilityState !== 'visible') return;
      // 아직 시작 전이면 곧 `start()` 가 돌면서 알아서 맞춰진다. 첫 로드의 `pageshow` 가
      // 헛된 탐침 하나를 쏘는 것도 이 문이 막는다.
      if (!view.started) return;
      if (resyncHoldRef.current) return;
      // **한 번의 복귀에 사건이 둘·셋 온다.** 바닥을 두지 않으면 그만큼 탐침이 나간다.
      const now = Date.now();
      if (now - lastProbeAtRef.current < RESUME_PROBE_MIN_INTERVAL_MS) return;
      lastProbeAtRef.current = now;
      resyncHoldRef.current = true;

      void (async () => {
        try {
          // **리시버가 쓰는 그 서버 주소다** — 탐침과 `resync()` 가 서로 다른 서버를
          // 보면 「맞췄는데 안 맞는」 상태가 되고 화면에는 아무 이유도 나오지 않는다.
          const serverResume = await fetchResumePoint(
            config.serverUrl,
            channelId,
            view.deviceId,
            auth.token,
          );
          if (dropped) return;

          // **지금 값을 읽는다.** 위 `await` 동안에도 영상은 간다.
          const elapsedSec = getElapsedSec();
          const currentProductId = view.item?.productId ?? null;
          const move = shouldResync({
            isGeneral,
            loggedIn: auth.token !== null,
            started: view.started,
            currentProductId,
            elapsedSec,
            serverResume,
            // v21. **광고 중에는 옮기지 않는다** — 판정은 `resume-return.ts` 가 갖는다.
            inAdBreak: view.adBreak !== null,
            // v33. **바이패스 중에도 옮기지 않는다** — 라이브는 정의상 엣지에 있다.
            inLive: view.live !== null,
          });
          setResumeCheck({
            atMs: Date.now(),
            by,
            serverResume,
            elapsedSec,
            currentProductId,
            moved: move,
          });
          if (!move) return;

          await resync();
          if (dropped) return;
          // 새 재생 맥락이다. 옛 (회차, 초)를 들고 있으면 첫 주기 보고가 「같은 자리」로
          // 판정되어 한 주기를 통째로 건너뛴다.
          reportStateRef.current = INITIAL_REPORT_STATE;
        } finally {
          resyncHoldRef.current = false;
        }
      })();
    };

    /*
     * ── 셋을 듣는 이유 ────────────────────────────────────────
     *
     * `visibilitychange` 하나로 충분해야 하지만 **그것이 확실한 것은 브라우저 탭까지**다.
     * WKWebView 로 감싼 셸에서 앱이 앞으로 나올 때 무엇이 오는지는 기기 없이 확인할 수
     * 없고, **오지 않으면 증상이 「이어보기가 안 된다」 하나**라 원인이 화면에 드러나지
     * 않는다. `pageshow`(bfcache)와 `focus` 를 함께 듣는 것이 그 미지수를 덮는 값싼 방법이다
     * — 중복은 `lastProbeAtRef` 가 먹고, 옮길 이유가 없으면 판정이 막는다.
     */
    const onVisible = onReturn('visibility');
    const onShow = onReturn('pageshow');
    const onFocus = onReturn('focus');
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onShow);
    window.addEventListener('focus', onFocus);
    return () => {
      dropped = true;
      resyncHoldRef.current = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onShow);
      window.removeEventListener('focus', onFocus);
    };
  }, [
    isGeneral,
    auth.token,
    channelId,
    view.deviceId,
    view.started,
    view.item?.productId,
    getElapsedSec,
    resync,
  ]);

  /*
   * ★ v21/F13 — **「알림을 보냈습니다」는 스스로 물러난다.**
   *
   * 오버레이를 치우는 규칙은 「상품이 바뀌면」(아래 효과)인데 **광고 중에는 `itemKey` 가
   * 바뀌지 않는다** — 그것이 시트를 살려 두려고 일부러 그렇게 둔 것이고(위 `healAfterClose`
   * 의 ★), 그 대가로 광고 중에 뜬 이 안내가 **광고가 끝나고 콘텐츠로 돌아온 뒤에도**
   * 남는다. 실기에서 「다음 회차」를 누를 때까지 남아 있었다.
   *
   * **버튼이 없는 층이라 스스로 닫아도 된다** — 텍스트와 작은 QR 뿐이라 리모컨 포커스가
   * 여기 갇히지 않는다(절대 규칙 32 가 `resume-toast` 를 버튼으로 만들지 않은 그 이유).
   *
   * **얼마나 남을지는 순수 함수가 정한다** (`handoffLingerMs`) — 계정 경로는 30초,
   * 가구 경로는 QR 이 유효한 동안이다.
   */
  useEffect(() => {
    const delay = handoffLingerMs(handoff, Date.now());
    if (delay === null) return;
    const timer = window.setTimeout(() => {
      // **그 사이에 다른 것이 떴으면 건드리지 않는다.**
      setHandoff((previous) => (previous === handoff ? HANDOFF_IDLE : previous));
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [handoff]);

  // **상품이 바뀌면 모든 오버레이를 닫는다** (`docs/06-frontend-spec.md` 「오버레이 규칙」).
  // 앞 상품의 QR 이 남아 있으면 스캔한 사람이 화면에 없는 상품을 사게 되고, 앞 상품의
  // "알림을 보냈습니다" 가 남으면 폰에 뜬 알림과 화면의 상품이 어긋난다.
  useEffect(() => {
    setHandoff(HANDOFF_IDLE);
    setInfoOpen(false);
    setSettingsOpen(false);
    // v23. **앞 상품의 폴백 QR 도 함께 치운다** — 남으면 스캔한 사람이 화면에 없는
    // 상품을 산다(이 효과가 애초에 막으려던 그것).
    setOfferFallback(null);
    // **채널 시트와 상품 목록 시트는 닫지 않는다.** 위 셋은 전부 **그 상품에 묶인**
    // 것이라 남으면 화면과 어긋나는데, 두 목록은 상품이 바뀌어도 그대로 옳다 — 고르는
    // 도중에 목록이 사라지면 시청자는 자기가 뭘 잘못 눌렀는지 알 수 없다.
    //
    // **상품 목록은 오히려 여기서 닫으면 안 된다** (v11). 선택이 곧 `itemKey` 를 바꾸므로
    // 여기에 넣으면 목록이 **선택의 부수 효과로** 닫히고, 그러면 「고르면 닫힌다」와
    // 「상품이 바뀌면 닫힌다」가 한 코드에 뭉쳐 `same` 경로(전환 없는 선택)에서 닫히지
    // 않는다. 닫는 것은 선택 핸들러의 일이다.
  }, [view.itemKey]);

  /**
   * ★ v21 — **광고가 열리고 닫힐 때 포커스를 회복한다** (절대 규칙 32).
   *
   * 컨트롤 바의 버튼 구성이 통째로 갈린다 — 「전체 회차」와 회차 스킵이 사라지고 광고
   * 구매·광고 스킵이 생긴다. 포커스가 사라진 버튼에 있었으면 **`<body>` 로 떨어지고**,
   * 그 상태에서는 방향키가 아무 데도 가지 않아 「리모컨이 죽었다」로 보인다.
   *
   * **`healAfterClose()` 를 새로 쓰지 않고 그대로 부른다** — 「활성 요소가 지금 층의
   * 후보가 아니면 첫 후보로」라는 규칙이 여기서도 정확히 옳다.
   *
   * ★ **`itemKey` effect 에 넣지 않는다.** 광고 중에는 `itemKey` 가 안 바뀌므로 **시트가
   * 광고를 넘어 살아남는데, 그것이 옳다**(시청자가 체크아웃 중일 수 있다). 여기 넣으면
   * 광고가 시작될 때마다 상품 시트가 닫힌다.
   */
  useEffect(() => {
    healAfterClose();
  }, [view.adBreak === null]);

  /**
   * ★ v23 — **카드가 걷힐 때 포커스를 회복한다** (절대 규칙 65 · `docs/47` §9.2).
   *
   * **뜰 때와 걷힐 때의 방향이 반대다.**
   *
   * - **뜰 때는 아무것도 하지 않는다** (D10). 시청자가 부르지 않은 이동이라 **리모컨을
   *   뺏는 것**이고, 마침 `[다음 회차 ▸]` 를 누르려던 사람은 상품을 누르게 된다.
   *   그래서 `OfferCard` 에 `autoFocus` 도 `.focus()` 도 없다.
   * - **걷힐 때는 치유한다.** 포커스가 사라진 버튼에 있었으면 **`<body>` 로 떨어지고**,
   *   3m 떨어진 TV 앞에서 그것은 **「리모컨이 죽었다」와 구분되지 않는다**(K4).
   *
   * ★★ **`healAfterClose()` 를 새로 쓰지 않고 그대로 부른다** — 「활성 요소가 지금 층의
   * 후보가 아니면 첫 후보로」라는 규칙이 여기서도 정확히 옳다. 새로 쓰면 층 판정이 두
   * 벌이 된다. (v21 이 이것을 export 했고 `WatchScreen` 이 이미 두 곳에서 쓴다.)
   *
   * **`offerKey` 로 거는 것이 요점이다** — 카드가 **다른 상품으로 바뀌는** 경우에도
   * 앞 버튼이 사라지므로, `null` 이 되는 순간만 보면 그 경로를 놓친다.
   *
   * ★★★ **그런데 `offerKey` 는 뜰 때도 바뀐다** (v23/O16 이 실기로 찾았다).
   *
   * `'' → cueId` 도 이 효과를 깨우고, 그때 포커스가 `<body>` 에 있으면 —
   * **진입 게이트를 막 지난 화면이 늘 그렇다** — `healAfterClose()` 가 「지금 층의 첫
   * 후보」로 옮기는데 그것이 **방금 생긴 카드**다. 위 D10 이 `autoFocus` 도 `.focus()`
   * 도 두지 않은 그 이유가 **치유를 타고 그대로 일어난다.**
   *
   * 증상은 「리모컨을 뺏겼다」 하나이고 **마우스를 쓰는 개발 기기에서는 드러나지
   * 않는다.** 그래서 **직전 키를 들고 있다가 「있던 것이 사라졌을 때만」 치유한다.**
   */
  const offerKey = view.offer?.cueId ?? '';
  const prevOfferKey = useRef('');
  useEffect(() => {
    const had = prevOfferKey.current;
    prevOfferKey.current = offerKey;
    // 뜰 때(`'' → cueId`)는 아무것도 하지 않는다. 걷힐 때와 바뀔 때만 치유한다.
    if (had === '') return;
    healAfterClose();
  }, [offerKey]);

  /**
   * ★ v23 — **상품 카드를 눌렀다** (`docs/47` §9.3 · 절대 규칙 45 의 v23 판).
   *
   * **일반 채널이 핸드오프 경로에 들어가는 둘째 자리다.** 갈리는 것은 **무엇을 눌렀는가**
   * 하나이고, 카드가 떠 있기만 하면 세 경로 전부 0건이다.
   *
   * ── 셋째 진입점을 그대로 둔다 ────────────────────────────
   *
   * `requestHandoff` 도 `requestAdHandoff` 도 **재사용하지 않는다** (`docs/47` §9.3).
   * 하나로 합치면 `triggerType` 별 분기가 그 함수 안에 셋 생기고, **그것이 정확히
   * 「하나로 합치면 한쪽만 고쳐진다」의 자리**다. 값이 넷 다르다 — `triggerType` ·
   * `campaignId` 의 출처 · 폴백 QR 이 읽을 URL · **봉투를 싣는다는 것.**
   *
   * ── `purchase_click` 을 내지 않는다 ──────────────────────
   *
   * 그것은 `EVENT_TYPES` 의 값이고 `getCurrentItem()` 에 귀속되므로, 상품 구매가
   * **드라마 회차의 구매 클릭**으로 기록된다 (절대 규칙 59·55). 상품 쪽 클릭은 브로커의
   * 트래킹이 맡는다.
   */
  const requestOfferHandoff = useCallback(() => {
    const offer = view.offer?.item;
    if (offer === undefined || handoff.kind === 'loading') return;

    /*
     * ★ **누른 순간의 상품을 붙든다** (v21/F13 이 실기에서 배운 것을 그대로 승계한다).
     *
     * 같은 기기(폰)면 응답이 `same_device` 이고 **시트가 열린다.** 그 시트가 무엇을
     * 여는지 말해 주지 않으면 화면이 「재생 중인 회차」로 채우는데, 일반 채널 회차에는
     * 판매처가 없다(`deepLink` 가 빈 문자열이다). 그 빈 값이 상대 URL 이 되어 **앱이
     * 자기 시작 게이트로 간다.**
     *
     * **왕복 동안 카드가 걷혔을 수 있으므로 여기서 담는다** — `displaySec` 이 15초다.
     * 시청자가 보려는 것은 **누를 때 보던 그 상품**이다.
     */
    const target: OfferSheetTarget = { productId: offer.offerId, campaignId: offer.offerId };
    // **클릭은 브로커의 트래킹으로 간다** (절대 규칙 55 승계) — `Event` 가 아니다.
    const clickUrls = offer.tracking.click;

    /*
     * ★ **폴백 QR 이 읽을 것을 함께 붙든다.**
     *
     * 바로 아래 `dismissOffer()` 가 카드를 걷으므로 **오류 오버레이가 그려질 때
     * `view.offer` 는 이미 `null` 이다** — 그때 QR 이 「재생 중인 회차」의 `deepLink`(`''`)를
     * 읽으면 **스캔한 사람이 아무 데도 못 간다.** 시트를 위해 `target` 을 붙드는 것과
     * **같은 이유이고 같은 순간**이다.
     *
     * **`landingUrl`(https)이지 `deepLink` 가 아니다** — QR 은 아무나 스캔하고 그 기기에
     * 앱이 없을 수 있다.
     */
    setOfferFallback({ offerId: offer.offerId, landingUrl: offer.landingUrl });

    // **눌렀으면 카드는 할 일을 다했다.** 그 큐는 다시 열리지 않는다.
    dismissOffer();
    trackOfferClick(clickUrls);
    setHandoff({ kind: 'loading' });

    void (async () => {
      try {
        const response = await requestPurchaseHandoff(
          runtime,
          buildOfferHandoffRequest({
            clientType,
            offer,
            // 지금 무엇을 보다가 눌렀는가 — 어트리뷰션이다.
            contentProductId: view.item?.productId ?? null,
            channelId,
            playlistId: view.playlistId,
            receiverDeviceId: view.deviceId,
            senderDeviceId: tv2m.init?.deviceId ?? null,
          }),
          { bloomServerUrl: config.serverUrl, token: auth.token },
        );
        setHandoff((previous) =>
          previous.kind === 'loading'
            ? sheetForOffer(handoffFromResponse(response), target)
            : previous,
        );
      } catch (error) {
        setHandoff((previous) =>
          previous.kind === 'loading' ? handoffFromError(error) : previous,
        );
      }
    })();
  }, [
    view.offer,
    view.item?.productId,
    view.deviceId,
    view.playlistId,
    handoff.kind,
    dismissOffer,
    tv2m.init,
    runtime,
    clientType,
    channelId,
    auth.token,
  ]);

  /**
   * `?debug=1` 일 때만 계측 핸들을 노출한다.
   *
   * 전환 수치는 30초에 한 번 나오고 무갭 여부는 눈으로 셀 수 없다. 자동 검증이 화면 밖에서
   * 같은 값을 읽을 수 있어야 M7 의 "M0 수치와 일치하는지 확인"이 사람의 눈이 아니라
   * 데이터로 성립한다. **패널이 쓰는 것과 같은 버퍼를 준다** — 별도 경로를 만들면
   * 검증이 보는 숫자와 화면에 뜨는 숫자가 갈라진다.
   *
   * T8 이 `clientType` 과 마지막 핸드오프 결과를 더했다. 판정이 틀리면 캐스케이드 전체가
   * 어긋나는데 **화면에는 그 사실이 드러나지 않으므로**, 하네스가 읽을 창이 필요하다.
   */
  useEffect(() => {
    if (!config.debug) return;
    window.__bloomReceiver = {
      view,
      telemetry,
      snapshot,
      /*
       * ★ v21/F13 — **광고 상태는 화면에 거의 드러나지 않는다.** 어느 시점이 이미
       * 발화했는지 · 최소 간격이 저장소에 있는지 · 브레이크 축의 경과가 몇 초인지는
       * 셋 다 눈으로 셀 수 없고, 그래서 하네스가 읽을 창이 필요하다.
       */
      adSnapshot,
      // v23. **화면에 증상이 없는 값 셋이 여기 있다** — `activeCount`(산술이 버린 큐의
      // 수) · `firedCues` · `pending`. 하네스가 읽을 창이 이것뿐이다.
      offerSnapshot,
      getAdBreakElapsedSec,
      getElapsedSec,
      /* v27 — **살아 있는** 재생 상태. `view` 는 렌더 스냅숏이라 시트가 열려 있으면 얼어붙는다 */
      getPlayhead,
      // v36 — 엔진만의 진단(Flower 면 조립된 재생목록 · 열리지 않은 브레이크). 엔진은 `view.engine`·`view.engineFallback`
      engineSnapshot,
      selectItem,
      clientType,
      handoff,
      resumeCheck,
    };
  });

  /**
   * `[구매하기]` · `[모바일에서 구매]`.
   *
   * **어떤 경우에도 재생을 멈추지 않는다** (절대 규칙 7·14). 오버레이나 시트만 바뀌고
   * 영상은 계속 간다 — 실패해도, 서버가 없어도 마찬가지다.
   *
   * ── B9 이 지운 한 줄 ────────────────────────────────────────
   *
   * 여기에 `window.location.href = response.deepLinkUrl` 이 있었다. Tier 0 에서 다른
   * 오리진의 상품 화면으로 옮기는 줄이었고, **그때 재생이 끝났다.** 그것은 설계 목표가
   * 아니라 웹의 제약이었다 — 시청 앱과 구매 앱이 갈려 있으니 옮길 수밖에 없었고, 옮기면
   * `<video>` 가 죽었다 (`docs/07` §5).
   *
   * B7 이 두 앱을 한 오리진으로 합치면서 그 제약이 사라졌다. 같은 기기면 **같은 화면
   * 위에 상품 시트를 열고** 영상은 축소된 채 계속 간다. 이제 **BLOOM 을 떠나는 유일한
   * 순간은 `[판매처로 이동]`** 이고, 그것은 시청자가 외부 판매처를 직접 고른 결과다 (B10).
   *
   * ── 왜 낙관적으로 먼저 여는가 ───────────────────────────────
   *
   * 같은 기기에서 살 수 있다는 것은 **서버에 묻기 전에 이미 안다** — 서버의 Tier 0 조건도
   * 이 화면과 같은 값 하나(`clientType === 'mobile_web'`)다. 그래서 왕복을 기다리지 않고
   * 열고, 응답은 `matchId` 를 채우거나 예상과 달랐음을 배지로 알릴 뿐 **시트를 닫지
   * 않는다** (`docs/14` B9 의 5번). 서버가 3초를 다 쓰거나 꺼져 있어도 구매 경로가 살아
   * 있는 것이 이 순서의 값어치다.
   *
   * 기존 BLOOM 이벤트(`purchase_click`)는 그대로 보낸다. TV2M 이 붙었다고 BLOOM 쪽 계측이
   * 끊기면 M7 이 세운 전환 지표가 그 지점에서 비교 불가가 된다.
   */
  const requestHandoff = useCallback(() => {
    const item = view.item;
    if (item === null || handoff.kind === 'loading') return;

    sendInteraction('purchase_click');

    // 같은 기기면 시트가 결과다. 아니면 서버가 무엇을 고르는지 기다린다.
    const sameDevice = clientType === 'mobile_web';
    if (sameDevice) {
      /*
       * **`same_device_handoff_clicked` 가 아니다** (`docs/13` §2.3 · §8). 그것은 딥링크로
       * 앱이 기동된 사건의 이름이고, 같은 앱 안에서 시트가 열린 것은 다른 사건이다.
       * 두 곳에서 세면 같은 전환이 두 벌이 된다.
       */
      tv2m.trackEvent('product_sheet_opened', {
        productId: item.productId,
        campaignId: campaignIdOf(channelId, item.productId),
        clientType,
      });
    }
    setHandoff(sameDevice ? HANDOFF_SHEET : { kind: 'loading' });

    void (async () => {
      try {
        const response = await requestPurchaseHandoff(
          runtime,
          {
            clientType,
            triggerType: 'channel_content',
            campaignId: campaignIdOf(channelId, item.productId),
            channelId,
            contentId: item.productId,
            productId: item.productId,
            // `positionSec` 은 보내지 않는다 (안 A, `docs/09` T8). 계약상 선택 필드이고,
            // 값을 얻으려면 `packages/bloom-receiver-sdk` 에 getter 를 더해야 하는데 **그 패키지의
            // 무변경이 이 확장의 완료 판정**이다. 재생 위치 분석이 필요해지면 안 C 로 승격한다.
            receiverDeviceId: view.deviceId,
            ...(view.playlistId === null ? {} : { playlistId: view.playlistId }),
            /*
             * **트리거를 낸 기기 자신을 발송 대상에서 뺀다** (절대 규칙 16).
             *
             * B7 이 이 필드를 필요하게 만들었다 — 통합으로 시청 기기도 TV2M `Device` 로
             * 등록되므로 **이것이 없으면 PC 에서 누른 알림이 그 PC 에 온다.** 서버는
             * B5 부터 이 값을 뺄 준비가 돼 있고, 여기가 그것을 알려 주는 유일한 자리다.
             *
             * BLOOM Server 의 `deviceId`(`receiverDeviceId`)와 다른 식별자다 — 저쪽은 시청 이벤트
             * 상관관계이고 이쪽은 가구 그래프의 기기다 (`docs/12` §6).
             */
            ...(tv2m.init === null ? {} : { senderDeviceId: tv2m.init.deviceId }),
          },
          /*
           * **`auth.token` 이 Tier 1 의 입력이다** — 서버는 이것으로만 계정을 알고,
           * 없으면 계정 매칭이 성립하지 않아 가구로 내려간다 (`docs/14` B6 의 1번).
           */
          { bloomServerUrl: config.serverUrl, token: auth.token },
        );

        /*
         * **늦게 도착한 응답이 화면을 되돌리지 못하게 한다.**
         *
         * 요청 사이에 상품이 바뀌었거나(그때 아래 효과가 `idle` 로 되돌린다) 시청자가
         * 시트를 닫았을 수 있다. 그 자리에 응답을 그대로 쓰면 **화면에 없는 상품의 QR 이
         * 다시 뜬다** — 스캔한 사람이 화면에 없는 상품을 사게 되는 그 결함이다.
         *
         * 그래서 이 요청이 만든 상태에 아직 머물러 있을 때만 반영한다.
         */
        setHandoff((previous) => {
          if (sameDevice) {
            return previous.kind === 'sheet' ? sheetFromOutcome(response) : previous;
          }
          return previous.kind === 'loading' ? handoffFromResponse(response) : previous;
        });
      } catch (error) {
        // **실패해도 시트는 닫지 않는다** (`docs/14` B9 의 5번). 같은 기기에서 살 수
        // 있다는 사실은 서버가 답하지 않아도 변하지 않는다 — 잃는 것은 `matchId` 뿐이다.
        setHandoff((previous) =>
          previous.kind === 'sheet'
            ? sheetWithNote(previous, { key: reasonKeyFromError(error) })
            : previous.kind === 'loading'
              ? handoffFromError(error)
              : previous,
        );
      }
    })();
  }, [
    view.item,
    view.deviceId,
    view.playlistId,
    handoff.kind,
    sendInteraction,
    tv2m.init,
    tv2m.trackEvent,
    runtime,
    clientType,
    auth.token,
  ]);

  /**
   * ★ v21 — **광고 중의 구매** (`docs/45` §9.4).
   *
   * **일반 채널이 핸드오프 경로에 들어가는 유일한 자리다** (절대 규칙 45 의 개정).
   * 갈리는 것은 **무엇을 재생 중인가** 하나이고, 본편 구간에서는 여전히 0건이다.
   *
   * **봉투를 실어 보낸다.** VAST 가 준 것을 그대로 넘기고 BLOOM 은 조회하지 않는다
   * (절대 규칙 53) — 본문 조립은 `buildAdHandoffRequest` 순수 함수가 한다.
   *
   * **`purchase_click` 을 내지 않는다.** 그것은 `EVENT_TYPES` 의 값이고 `getCurrentItem()`
   * 에 귀속되므로, 광고 구매가 **드라마 회차의 구매 클릭**으로 기록된다 (절대 규칙 50·55).
   * 광고 쪽 클릭은 VAST 트래킹이 맡는다.
   */
  const requestAdHandoff = useCallback(() => {
    const ad = view.adBreak?.item;
    if (ad === undefined || handoff.kind === 'loading') return;

    /*
     * ★ **누른 순간의 광고를 붙든다** (v21/F13, 실기가 드러냈다).
     *
     * 같은 기기(폰)면 응답이 `same_device` 이고 **시트가 열린다.** 그 시트가 무엇을 여는지
     * 말해 주지 않으면 화면이 「재생 중인 회차」로 채우는데, 광고 중의 그것은 **드라마
     * 회차**이고 일반 채널 회차에는 판매처가 없다(`deepLink` 가 빈 문자열이다).
     * 그 빈 값이 상대 URL 이 되어 **앱이 자기 시작 게이트로 갔다** — `AdSheetTarget` 주석.
     *
     * 왕복 동안 광고가 넘어갔을 수 있으므로 **여기서** 담는다. 시청자가 보려는 것은 누를
     * 때 보던 그 광고다.
     */
    // v38 — 본문은 `runAdHandoff`(ONAIR CSPM 과 같이 쓴다 · 그대로 옮겼다)
    setHandoff({ kind: 'loading' });

    void runAdHandoff({
      runtime,
      clientType,
      ad,
      contentProductId: view.item?.productId ?? null,
      channelId,
      playlistId: view.playlistId,
      receiverDeviceId: view.deviceId,
      senderDeviceId: tv2m.init?.deviceId ?? null,
      token: auth.token,
    }).then((next) => {
      setHandoff((previous) => (previous.kind === 'loading' ? next : previous));
    });
  }, [
    view.adBreak?.item,
    view.item?.productId,
    view.deviceId,
    view.playlistId,
    handoff.kind,
    tv2m.init,
    runtime,
    clientType,
    channelId,
    auth.token,
  ]);

  /**
   * 「전체 상품」에서 고른 상품 (v11, `docs/27` D1).
   *
   * ── 순서가 계약이다 ─────────────────────────────────────────
   *
   * ```
   * 1. resolvePick — 지금 보는 상품이면 전환하지 않는다 (D5)
   * 2. selectItem  — 제2 플레이리스트 · select 이벤트 · 목록 회전 (절대 규칙 33)
   * 3. 목록을 닫는다
   * 4. 구매 버튼으로 포커스를 옮긴다. **누르지는 않는다** (절대 규칙 35)
   * ```
   *
   * **선택이 구매보다 먼저인 것이 이 순서의 요지다.** SDK 는 `purchase_click` 을 언제나
   * `getCurrentItem()` 에 귀속시키므로(`core/receiver.ts`), 재생하지 않고 목록에서 바로
   * 사면 그 이벤트가 **여전히 재생 중이던 다른 상품에 붙는다** — 화면에는 드러나지 않고
   * 분석에서만 갈린다. 선택을 먼저 하면 그 문제가 존재하지 않고, `requestHandoff` 를
   * 손댈 필요도 없다.
   *
   * **`selectItem` 은 던지지 않는다.** 실패하면 코어가 `error` 로 알리고 `view.fatalError`
   * 가 화면을 덮는다 — 그때는 컨트롤 바 자체가 없으므로 4번의 옵셔널 체이닝이 조용히
   * 아무 일도 하지 않는다. 그것이 맞는 동작이다.
   */
  /**
   * 구매 버튼으로 포커스를 옮긴다 — **성공할 때까지, 성공했는지 확인하며** (절대 규칙 35).
   *
   * **한 프레임만 기다리면 조용히 실패한다** (2026-08-09 실기). 목록이 열려 있는 동안
   * `.stage--mini .controls { display: none }` 이라 구매 버튼은 **포커스를 받을 수 없고**,
   * 시트를 닫은 뒤 React 가 커밋하기 전에 `requestAnimationFrame` 이 돌면 `.focus()` 가
   * 아무 일도 하지 않는다. `HTMLElement.focus()` 는 **실패를 알려 주지 않는다** — 그래서
   * 증상이 「어떤 때는 링이 보이고 어떤 때는 안 보인다」 하나였고, 두 번에 한 번꼴로 났다.
   *
   * **그 원인은 2026-08-10 에 사라졌다** — 목록이 `.sheet` 로 옮겨 가 컨트롤 바를 숨기지
   * 않으므로 지금은 첫 프레임에 성공한다. **그래도 지우지 않는다.** 없애는 이득이 0 이고,
   * 배치가 다시 바뀌는 날 같은 침묵이 그대로 돌아온다 (`docs/27` D10).
   *
   * 그래서 둘을 더한다.
   *   · **그려졌는지 먼저 본다** — `getClientRects().length` (`use-tv-focus.ts` 의 그 판정)
   *   · **옮겨졌는지 확인한다** — `document.activeElement` 대조. 아니면 다음 프레임에 다시
   *
   * 상한을 두는 이유는 **영영 오지 않는 경우가 정상이기 때문**이다 — `selectItem` 이
   * 실패하면 `view.fatalError` 가 화면을 덮고 컨트롤 바 자체가 없다. 그때는 조용히
   * 그만두는 것이 맞다.
   *
   * `preventScroll` — 컨트롤 바는 이미 화면 안이라 스크롤할 이유가 없다.
   */
  const focusAfterPick = useCallback(() => {
    /*
     * **일반 채널에는 구매 버튼이 없다** (절대 규칙 35 의 예외). `purchaseRef` 를 그대로
     * 폴링하면 영원히 `null` 이라 **30프레임을 헛돌고 포커스가 사라진다.**
     *
     * ★ v21 — **분기가 셋이 됐다.** 광고 중에는 「전체 회차」도 구매도 화면에 없다.
     * 그때 어느 ref 를 폴링해도 30프레임을 헛도므로 **아예 옮기지 않고** 층 회복에
     * 맡긴다(`healAfterClose`) — 광고 중에는 목록 시트가 애초에 열리지 않으므로
     * (`onOpenProducts` 가 `null`) 이 경로를 밟을 일도 사실상 없다.
     */
    if (adBreakRef.current !== null) {
      healAfterClose();
      return;
    }
    const ref = isGeneral ? allProductsRef : purchaseRef;
    let frames = 0;
    const tick = () => {
      const el = ref.current;
      if (el !== null && el.getClientRects().length > 0) {
        el.focus({ preventScroll: true });
        if (document.activeElement === el) return;
      }
      if (++frames < 30) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isGeneral]);

  /**
   * 편성표로 세워진 화면의 포커스 (v25, `docs/51` §7.5).
   *
   * ★ **떠나는 화면에서 `focusAfterPick` 을 부르면 반드시 실패한다.** 그 함수의
   * 30프레임(≈0.5초) 폴링은 **「그려졌지만 커밋 전」을 견디려고 만든 것이지 네트워크
   * 왕복을 견디려고 만든 것이 아니다** — 새 화면이 버튼을 그리려면 그 앞에 `/playlist`
   * 왕복 + `prepareNext`(상한 15초) + 스왑이 있다. 함수를 고치지 않고 **부르는 시점**을
   * 옮긴다.
   *
   * **셋을 다 기다린다.** `channelKind` 가 아직 `null` 이면 `isGeneral` 이 거짓이라
   * `purchaseRef` 를 폴링하는데, 일반 채널에서는 그 ref 가 영원히 `null` 이라
   * **30프레임을 헛돈다** (절대 규칙 35 가 경고하는 그 모양).
   */
  /**
   * v26 — **홈쇼핑 채널의 시작점을 기억한다** (절대 규칙 70 · 자물쇠 ①).
   *
   * ★ **홈쇼핑일 때만 쓴다.** 일반 채널의 항목이 저장되면, 언젠가 그 값이
   * `start({ from })` 으로 흘러 서버가 `positionSec: 0` 을 내고 **저장된 이어보기 지점을
   * 덮는다 — 되돌릴 방법이 없다.** 순수 함수 쪽(`entryStartFor`)에 자물쇠가 하나 더 있는
   * 이유는 **하나면 그 하나를 지운 커밋이 조용히 통과하기** 때문이다.
   *
   * **항목이 바뀔 때마다 쓴다.** `pagehide` 에만 걸면 iOS 에서 신뢰할 수 없고
   * (`beforeunload` 를 쓰지 않는 그 이유), 무엇보다 **상품 하나가 몇 분이라 이 주기가
   * 저장소 쓰기로 충분히 드물다.**
   *
   * **광고 중에도 `view.item` 은 직전 회차 그대로다**(절대 규칙 50) — 홈쇼핑에는 광고가
   * 없으니(규칙 56) 여기서는 성립하지 않지만, 값이 언제나 콘텐츠라는 것이 이 훅이
   * 크리에이티브 id 를 저장할 수 없는 이유다.
   */
  useEffect(() => {
    if (view.channelKind !== 'homeshopping') return;
    const productId = view.item?.productId;
    if (productId === undefined || productId === '') return;
    writeLastItem(channelId, productId);
  }, [view.channelKind, view.item, channelId]);

  const pendingEpgFocus = useRef(startAt !== null);
  useEffect(() => {
    if (!pendingEpgFocus.current) return;
    if (!view.started || view.item === null || view.channelKind === null) return;
    pendingEpgFocus.current = false;
    // 광고 중이면 이 함수가 스스로 `healAfterClose()` 로 간다 — 분기가 이미 셋이다.
    focusAfterPick();
  }, [view.started, view.item, view.channelKind, focusAfterPick]);

  /**
   * 옆 채널로 옮긴다 (v25 의 ▲▼ 와 v26 의 숫자 키가 **같은 함수를 탄다**).
   *
   * **셋의 순서가 계약이다.**
   *
   * ① `unlockBuffers()` — v26. **게이트에서 숫자 키를 누르는 경로 때문이다.** 그때 실제
   *    `start()` 는 새 화면의 `useEffect` 안이라 **제스처 밖**이고, 그것이 v12 가 채널
   *    전환에서 만난 모양이다 — 증상은 「눌렀는데 아무 일도 안 일어난다」 하나. v25 가
   *    게이트의 편성표 카드에 대해 만든 그 처방을 그대로 쓴다 (`docs/51` §5.3-c).
   *    **이미 재생 중이면 아무 일도 하지 않는다** — 그 함수가 스스로 그렇게 짜여 있다.
   * ② `reportEvent('channel_switch')` — **전환 전에 보고한다.** 전환은 이 화면을 통째로
   *    언마운트하므로 그 뒤에는 보낼 주체가 없다. 빠뜨리면 떠나는 채널의 지점이 안 남고,
   *    증상은 「재핑하고 돌아오면 처음부터」 하나다.
   * ③ `onSwitchChannel(next)` — **`startFromProductId` 를 주지 않는다.** 옆 채널로 가는
   *    것은 「그 채널을 켜는」 일이고, 그러면 서버가 이어보기를(홈쇼핑이면 기기가 기억한
   *    시작점을) 실어 준다 (절대 규칙 68·70).
   *
   * 같은 채널이면 아무 일도 하지 않는다 — `switchTo` 의 가드에 걸려 무동작이 되지만,
   * ②가 그 앞에 있어 **누르지 않은 전환이 지점 보고를 만든다.**
   */
  /**
   * ★ v32 — **「홈」** (`docs/63` §3.3 ⑥ · 절대 규칙 82). 나가는 것은 **진행 보고 한 번**
   * (`teardown` — `pagehide` 의 `flush` 와 같은 몸체 · 같은 `reportEvent`)이고 **이벤트는
   * 없다** — 채널 전환이 아니다(`docs/63` §11-14). 리시버 파괴는 `ChannelGate` 가 이 화면을
   * 내리면서 한다(규칙 27). 홈에서 되돌아올 때 편성표는 `HOME_REFRESH_DELAY_MS` 뒤에 다시
   * 받는다 — 이 보고와의 경합 때문이다(규칙 73 ②).
   */
  const goHome = useCallback(() => {
    reportEvent('teardown');
    onHome();
  }, [reportEvent, onHome]);

  const zapTo = useCallback(
    (next: string) => {
      if (next === channelId) return;
      unlockBuffers();
      reportEvent('channel_switch');
      onSwitchChannel(next);
    },
    [channelId, reportEvent, onSwitchChannel],
  );

  /**
   * v26 — 숫자 키로 채널을 옮긴다 (절대 규칙 71).
   *
   * **채널이 하나뿐이면 훅을 끄지 않는다** — 켜 두어도 `channelForNumber` 가 자기 자신을
   * 주고 `zapTo` 의 첫 줄이 그것을 버린다. 조건을 여기 두면 「채널이 둘 이상일 때만」이
   * 두 자리가 된다(▲▼ 가 이미 그 조건을 갖고 있다).
   */
  useChannelNumberKeys(
    useCallback(
      (number: number) => {
        const next = channelForNumber(epgRows, number);
        if (next === null) return;
        zapTo(next);
      },
      [epgRows, zapTo],
    ),
  );

  const pickProduct = useCallback(
    (picked: string) => {
      const outcome = resolvePick(view.item?.productId ?? null, picked);
      // 2026-10-29 · 잠겼으면 보고도 선택도 하지 않는다 — 보고가 먼저 나가면 고르지 않은 회차가 이어보기 지점이 된다
      if (outcome.kind === 'switch' && isRecordedLocked()) {
        setProductSheetOpen(false);
        setLockToast((n) => n + 1);
        return;
      }

      void (async () => {
        if (outcome.kind === 'switch') {
          /*
           * **고른 회차를 0 초로 보고한다** (v18).
           *
           * 진행이 (계정, 채널) **한 지점**이라, 지금 보고 있던 회차를 보고하면 **시청자가
           * 떠나는 회차가 그 채널의 지점이 된다** — 그리고 다음 주기 보고까지 **10초 동안**
           * 그렇게 남는다. 그 사이에 채널을 옮기거나 앱을 닫으면 **고른 것이 아니라 옛
           * 회차로 되돌아간다.**
           *
           * 서버가 방금 준 답과 같은 것을 쓰는 것이기도 하다 — 선택의 `resume` 은 언제나
           * `{ 고른 회차, 0 }` 이다(v18 규칙 1).
           *
           * **`select` 사유여야 한다.** 서버에서 되감기가 허용되는 유일한 값이라, 이것이
           * 아니면 「처음부터 다시 보기」가 단조 증가 규칙에 막힌다.
           */
          reportEvent('select', { productId: outcome.productId, elapsedSec: 0 });
          await selectItem(outcome.productId);
        }

        setProductSheetOpen(false);
        focusAfterPick();
      })();
    },
    [view.item?.productId, selectItem, focusAfterPick, reportEvent, isRecordedLocked],
  );

  const toggleInfo = () => {
    setInfoOpen((open) => {
      if (!open) sendInteraction('info_view');
      return !open;
    });
  };

  const item = view.item;

  /**
   * 목록 행. **판정은 순수 함수가 한다** — 이 패키지는 node 환경에서 테스트하므로
   * 화면 안에서 걸러거나 정렬하면 그것이 검사 밖에 놓인다 (`product-list.ts`).
   */
  const productRows = useMemo(
    () => toProductRows(view.items, item?.productId ?? null),
    [view.items, item?.productId],
  );
  /** v39 — 「콘텐츠」 시트의 모양과 1단계 줄. 고를 것이 하나뿐이면 `'flat'`(지금의 시트 그대로 · D8) */
  const contentMode = isGeneral ? contentSheetMode(view.items, catalog) : 'flat';
  const contentRows = useMemo(
    () => (catalog === null ? [] : toContentRows(view.items, item?.productId ?? null, catalog)),
    [view.items, item?.productId, catalog],
  );

  /*
   * **구매 시트가 열려 있는 동안만 영상을 줄인다.** 클래스 하나로 갈리고 나머지는 CSS 다
   * (`MiniPlayer.css`) — SDK 는 화면 크기를 모르고, 그것이 계층 분리의 이득이다.
   *
   * **v11 은 여기에 `productSheetOpen` 을 함께 두었다** — 「전체 상품」 목록도 같은 자리에
   * 서므로 목록에서 골라 구매로 이어질 때 영상 크기가 이어지게 하려던 것이다.
   * 2026-08-10 에 뺐다: 목록이 채널 시트와 같은 층으로 옮겨 가 **영상을 축소하지 않고
   * 덮는다** (`ProductListSheet.tsx` · `docs/27` D3 의 개정).
   */
  /**
   * v29. **마운트 시점에 잡아 둔 프레임이 있는가** — 채널 전환으로 들어왔다는 뜻이다.
   * 한 번만 읽는다(`useState` 의 지연 초기화): 렌더마다 읽으면 걷힌 뒤에도 이 값이
   * 참인 렌더가 섞인다.
   */
  const [frozen, setFrozen] = useState(() => freezeFrameHeld());

  /*
   * ★ v29. **편성표에서 고른 전환은 새 채널이 나올 때 걷힌다** (2026-09-06 무대 신고).
   *
   * 「전환이 시작됐다」로 닫으면 그 사이를 **잡아 둔 옛 채널의 프레임**이 덮어
   * 「눌렀더니 원래 채널이 한 번 더 나온다」가 된다 — 그것이 신고된 그림이다.
   * 판정은 `closesSwitchEpg` 하나이고 재료가 셋이다.
   *
   * **`epgOpen` 으로 들어온 경우에만 돈다** — 시청자가 손으로 연 시트를 재생이
   * 붙었다고 걷으면 **둘러보던 중에 화면이 닫힌다.**
   */
  const openedBySwitch = useRef(epgOpen);
  useEffect(() => {
    if (!openedBySwitch.current || !channelSheetOpen) return;
    if (
      closesSwitchEpg({
        showing: view.state === 'playing',
        frozen,
        failed: view.fatalError !== null,
      })
    ) {
      openedBySwitch.current = false;
      setChannelSheetOpen(false);
    }
  }, [channelSheetOpen, view.state, view.fatalError, frozen]);

  const mini = handoff.kind === 'sheet';

  return (
    <div className={`stage${mini ? ' stage--mini' : ''}`}>
      <div className="stage__video" ref={setContainer} />
      {/*
        v28. 새 채널의 첫 프레임이 붙을 때까지 직전 채널의 마지막 프레임을 덮는다.
        v29. **걷히는 순간을 받는다** — 그동안 로딩 문구를 숨기기 때문이고, 모듈 상태를
        렌더에서 읽기만 하면 걷혀도 다시 그려지지 않는다.
      */}
      <FreezeFrame
        onRelease={() => {
          setFrozen(false);
        }}
      />

      {view.started && item !== null && (
        <>
          {/*
            좌상단 「남음 / 전체」 (v12). **버튼이 아니다** — 리모컨 포커스 후보는
            `button:not([disabled]), a[href]` 뿐이라 링에 들지 않는다 (절대 규칙 29).

            **이 프래그먼트 안에 두는 것이 자리다.** 진입 전(`.gate`)·로딩·오류(`.notice`)
            는 뒤에 오는 `inset:0` 스크림이라 저절로 덮고, 시트 셋(z-31)도 덮는다.

            `durationSec` 은 플레이리스트가 준 값이라 **언제나 확정이다** — 서버가
            그것이 비어 있는 상품을 애초에 내려보내지 않는다 (절대 규칙 1).
          */}
          {/*
            v21. **시계가 두 갈래다.** 갈리는 것은 **무엇을 세는가**이고 문구는 사전이 낸다.

            ★ **본편은 `item.durationSec` — 콘텐츠 축이다** (절대 규칙 57).
            `effectiveDurationSec` 을 여기 쓰면 「42분」이 「48분」이 되고, 시청자는 이 시계로
            아무것도 계획할 수 없다. 광고는 매번 다르고 청크는 콘텐츠의 것이다.

            광고는 **그 pod 의 실제 길이 합**(`totalSec` — 정책 길이가 아니다)과
            **브레이크 축의 경과**를 쓴다. 스킵하면 그 경과가 불연속으로 는다.
          */}

          {/*
            이어보기 토스트 (v16). **버튼이 아니다** — 3초 뒤 사라지는 요소에 리모컨
            포커스가 갇히면 그 층에서 빠져나올 수 없다 (`docs/38` §6.7).

            `scope === 'device'` 면 문구가 다르다 — 「이 기기에서만 기억한다」를 알려
            로그인의 값을 그 자리에서 보인다.
          */}
          {resumeToast !== null && (
            <div className="resume-toast" role="status" data-testid="resume-toast">
              {/* v18. 축이 계정 하나라 문구도 하나다 — 「이 기기에서만」이 사라졌다 */}
              {t('watch.resumeToast', {
                episode: episodeLabel(t, item),
                time: formatClock(resumeToast.positionSec),
              })}
            </div>
          )}

          {lockToast !== 0 && (
            <div className="resume-toast" role="status" data-testid="recorded-locked-toast">
              {t('watch.recordedLocked')}
            </div>
          )}

          {infoOpen && <InfoOverlay item={item} />}

          {/*
            ★ v23 — 상품 오버레이 (`docs/47` §9.1 · 절대 규칙 59·65).

            **화면이 이것을 그리는 유일한 근거가 `view.offer` 다.** `view.items` 에도
            `view.item` 에도 상품은 없다 — 섞으면 「전체 회차」 시트가 상품을 그린다.

            **광고 중에는 코어가 이미 걷었다** — 여기서 `view.adBreak === null` 을 다시
            보지 않는 이유가 그것이다(판정이 두 곳에 생긴다). 브레이크가 열리면
            `enterAdBreak` 의 첫 줄이 `closeOffer()` 를 부르고 `view.offer` 가 `null` 이 된다.

            **`data-focus-scope` 를 붙이지 않는다** — 기본 층에 버튼 둘이 늘 뿐이다
            (규칙 65 의 셋째).
          */}
          {view.offer !== null && (
            <OfferCard view={view.offer} onPick={requestOfferHandoff} onDismiss={dismissOffer} />
          )}

          {/*
            **Tier 0 의 도착지** (절대 규칙 14). v7 은 이 자리에서 페이지를 떠났다.
            영상은 위로 축소된 채 계속 가고, 닫으면 전체화면으로 돌아온다 — 재생이 멈춘
            적이 없으므로 되돌릴 것이 화면 크기뿐이다.
          */}
          {handoff.kind === 'sheet' && (
            <ProductSheet
              /*
               * ★ **무엇을 여는지는 상태가 말한다** (v21/F13). 전에는 이 자리가 재생 중인
               * 회차로 **고정**이었고, 광고가 그 전제를 깼다 — `handoff.ts` 의
               * `AdSheetTarget` 주석이 그 증상을 적는다.
               */
              context={sheetLinkContext(handoff, {
                productId: item.productId,
                campaignId: campaignIdOf(channelId, item.productId),
              })}
              note={handoff.note}
              checkout={tv2m}
              // **광고 시트는 이 주소로 소재를 읽는다** (v21). 콘텐츠 시트에서는 쓰이지
              // 않는다 — 그쪽 상품은 BLOOM Server 에서 온다.
              tv2mServerUrl={runtime.tv2mServerUrl}
              /*
               * **추적 4파라미터 중 둘이 여기서 온다** (절대 규칙 8). 시청 세션이 살아
               * 있는 유일한 경로라, `[판매처로 이동]` 이 만드는 URL 에 `d=`·`pl=` 이
               * 채워지는 것도 이 경로뿐이다 — Push·QR 로 온 사람의 추적은 서버측
               * 역참조(`match_id`)가 맡는다 (`checkout.ts`).
               */
              tracking={{ deviceId: view.deviceId, playlistId: view.playlistId }}
              onClose={() => {
                setHandoff(HANDOFF_IDLE);
              }}
            />
          )}

          {handoff.kind === 'push' && (
            /*
             * **한 오버레이가 둘을 함께 그린다** (`docs/21` W8). 가구 경로는 Push 와 QR 을
             * 같이 내므로 시청자에게는 「알림을 보냈다」와 「안 울리면 이걸 찍어라」가 한
             * 화면에 있어야 한다 — 따로 띄우면 하단 1/3 규칙을 넘어 재생 화면을 가린다
             * (`docs/06` 「오버레이 규칙」).
             */
            <PushSentOverlay
              count={handoff.count}
              {...(handoff.source ? { source: handoff.source } : {})}
              {...(handoff.qrUrl ? { qrUrl: handoff.qrUrl } : {})}
            />
          )}
          {handoff.kind === 'qr' && <QrOverlay url={handoff.url} />}
          {handoff.kind === 'error' && (
            <HandoffErrorOverlay
              reason={handoff.reason}
              /*
               * 폴백은 BLOOM Server 의 간이 랜딩이다 — TV2M 이 통하지 않아도 살 방법이 있어야
               * 하고, 추적 4파라미터가 붙는 그 경로는 여전히 유효하다 (절대 규칙 8).
               * `qr.ts` 를 지우지 않은 이유가 이 한 줄이다 (`docs/09` T8).
               */
              fallbackUrl={
                view.playlistId === null
                  ? null
                  : /*
                     * ★ v21. **광고의 폴백 QR 은 `clickThrough`(https)다** — `deepLink` 가
                     * 아니다. 커스텀 스킴을 QR 에 넣으면 **스캔한 폰에서 안 열린다**
                     * (`docs/45` §14 의 17). 그 둘은 데모에서 잘 안 밟는 경로라
                     * TV 에서는 아무 증상이 없다.
                     *
                     * 광고 중에는 상품 축도 광고의 것이다 — `productId` 에 크리에이티브
                     * id 가 실린다(폰이 그 값으로 사본을 읽는다).
                     */
                    /*
                     * ★ v23. **상품의 폴백 QR 은 `offer.landingUrl`(https)다** —
                     * `deepLink` 가 아니다. 커스텀 스킴을 QR 에 넣으면 **스캔한 폰에서
                     * 안 열린다**(v21 §14 의 17 이 광고에서 밟은 그것).
                     *
                     * **QR 은 아무나 스캔하고 그 기기에 앱이 없을 수 있다** — 그래서
                     * 여기만 웹 랜딩이고, 핸드오프 봉투의 `landingUrl`(= 딥링크)과
                     * **다른 값을 고른다.** 그 둘이 같은 이름인 것이 이 자리를 헷갈리게
                     * 만드는데, 뜻은 「폰이 열 곳」과 「아무나 스캔할 곳」으로 다르다.
                     *
                     * **상품 축도 상품의 것이다** — `productId` 에 `offerId` 가 실린다
                     * (폰이 그 값으로 사본을 읽는다).
                     */
                    offerFallback !== null
                    ? purchaseQrUrl(offerFallback.landingUrl, {
                        deviceId: view.deviceId,
                        productId: offerFallback.offerId,
                        playlistId: view.playlistId,
                      })
                    : view.adBreak !== null
                      ? purchaseQrUrl(view.adBreak.item.clickThrough, {
                          deviceId: view.deviceId,
                          productId: view.adBreak.item.creativeId,
                          playlistId: view.playlistId,
                        })
                      : purchaseQrUrl(item.deepLink, {
                          deviceId: view.deviceId,
                          productId: item.productId,
                          playlistId: view.playlistId,
                        })
              }
            />
          )}

          <ControlBar
            clientType={clientType}
            channelKind={view.channelKind}
            channelName={epgRows.find((r) => r.channel.channelId === channelId)?.channel.name ?? null}
            channelNumber={
              epgRows.find((r) => r.channel.channelId === channelId)?.channel.number ?? null
            }
            onOpenChannels={
              epgRows.length > 1
                ? () => {
                    // **여는 순간 다시 받는다** (v27/TG15 · `ChannelGate` 의 ★★)
                    onEpgOpen();
                    setChannelSheetOpen(true);
                  }
                : null
            }
            /*
             * 채널 재핑 (v25). **편성표를 거치지 않고 바로 옆 채널로 옮긴다.**
             *
             * ★ **전환 전에 보고한다** — 전환은 이 화면을 통째로 언마운트하므로 그
             * 뒤에는 보낼 주체가 없다. 빠뜨리면 **떠나는 채널의 지점이 안 남고**,
             * 증상은 「재핑하고 돌아오면 처음부터」 하나다 (`ChannelSheet` 의 그 계약).
             *
             * **`startFromProductId` 를 주지 않는다** — 옆 채널로 가는 것은 「그 채널을
             * 켜는」 일이고, 그러면 서버가 이어보기 지점을 실어 준다 (절대 규칙 68).
             */
            onZapChannel={
              epgRows.length > 1
                ? (step) => {
                    const next = neighborChannel(epgRows, channelId, step);
                    if (next === null) return;
                    zapTo(next);
                  }
                : null
            }
            /*
             * **편성이 1건뿐이면 그리지 않는다** — 고를 것이 없고, 유일한 항목은 지금
             * 재생 중이라 눌러도 아무 일도 일어나지 않는다. 채널 버튼과 같은 판단이다.
             */
            /*
             * v21. **광고 중에는 열지 않는다** (`docs/45` §9.2). 광고를 보다가 회차를
             * 고르면 그 pod 이 통째로 사라지고 임프레션도 함께 잃는다 — 그리고 광고 중에
             * 「전체 회차」를 여는 것은 시청자에게도 이상하다.
             */
            onOpenProducts={
              // v39. 편성이 하나뿐이어도 라이브러리가 있으면 고를 것이 있다(`contentMode`)
              (productRows.length > 1 || contentMode === 'series') &&
              view.adBreak === null &&
              view.live === null
                ? () => {
                    setProductSheetOpen(true);
                    // 시트를 열 때 다시 받는다 — 운영자가 그 사이에 시리즈를 공개했을 수 있다(규칙 73 ② 의 편성표와 같은 이유)
                    if (isGeneral) loadCatalog();
                  }
                : null
            }
            contentLevels={contentMode === 'series'}
            onHome={goHome}
            purchaseRef={purchaseRef}
            allProductsRef={allProductsRef}
            handoffOpen={handoff.kind !== 'idle' && handoff.kind !== 'loading'}
            handoffPending={handoff.kind === 'loading'}
            infoOpen={infoOpen}
            skip={view.skip}
            adBreak={view.adBreak}
            // v33. **없는 것 여섯이 이 한 줄로 갈린다** (절대 규칙 85)
            live={view.live}
            /*
             * v34-b. **「라이브 ↔ 이어보기」** — 편성표의 두 칸과 **같은 문**이다(`epgPick` 의
             * `live`·`resumeRecorded` 갈래). 진행 보고는 여기서 내지 않는다 — 들어가는 순간의
             * 보고와 떠날 때의 `teardown` 이 바이패스 가드를 타고 이미 나간다(규칙 84).
             */
            liveSwitch={view.liveSwitch}
            onSwitchLive={() => {
              if (view.liveSwitch === 'live') void joinLive();
              else if (view.liveSwitch === 'recorded') void resumeRecorded();
            }}
            onRequestHandoff={requestHandoff}
            onRequestAdHandoff={requestAdHandoff}
            onToggleInfo={toggleInfo}
            /*
             * v21. **광고 스킵은 진행 보고를 내지 않는다** — 회차를 건너뛴 것이 아니다.
             * `reportEvent('skip')` 이 여기 붙으면 광고 스킵이 **회차 이탈**로 기록된다.
             */
            onSkipAd={skipAd}
            onSkip={() => {
              // **건너뛴 회차의 진행은 그 시점의 위치로 남는다** — 「건너뛴 것」을
              // 「다 본 것」으로 만들지 않는다. 나중에 목록에서 돌아올 수 있어야 한다.
              reportEvent('skip');
              skip();
            }}
            /*
             * ★ v35. **녹화물을 재생 중일 때만** (`docs/71` · 절대 규칙 90). 조건은 코어의
             * `canControlPlayback()` 과 같은 넷이다 — 재생 중 · 광고 아님 · 라이브 아님 · 회차 있음.
             * 넷 중 하나라도 거짓이면 리시버가 어차피 무동작이고, 화면은 그때 버튼을 **그리지 않는다.**
             *
             * **진행 보고를 따로 내지 않는다** — 탐색한 자리는 주기 보고와 `teardown` 이 나른다
             * (`PROGRESS_REPORT_REASONS` 그대로 · 규칙 90).
             */
            transport={
              view.state === 'playing' &&
              view.adBreak === null &&
              view.live === null &&
              view.item !== null
                ? {
                    paused: view.paused,
                    onTogglePause: view.paused ? resumePlayback : pausePlayback,
                    onSeek: seekBy,
                  }
                : null
            }
          />

          {/*
            ★ **시계와 오디오 토글이 한 묶음이다** (v22). 배치·`?debug=1` 오프셋·
            `pointer-events` 를 이 래퍼가 지고, 시계는 생김새만 남는다 — 버튼을 고정
            오프셋으로 둘 수 없어서다(시계 폭이 언어마다 다르다).

            ★★ **`<ControlBar>` 뒤에 선언하는 것이 자리다.** 절대 위치라 보이는 곳은
            좌상단 그대로이고, 갈리는 것은 **진입 포커스**다 — `entryFocusIndex` 가
            **기하가 아니라 DOM 순서의 가장 앞**을 착지점으로 삼으므로
            (`tv-focus/geometry.ts`), 시계 자리(위)에 두면 **재생을 시작하고 처음 누른
            화살표가 채널 버튼이 아니라 음소거에 착지한다.** 그 함수 주석이 설정 버튼에
            대해 적어 둔 사고와 같은 모양이다.

            `MuteButton` 은 `volume` 을 못 만지는 기기(아이폰)에서 **스스로 null 을
            돌려준다** — `disabled` 로 두면 포커스가 빈 자리로 간다(절대 규칙 29).
          */}
          <div className={`stage__topleft${config.debug ? ' stage__topleft--debug' : ''}`}>
            {/*
              ★ v33. **바이패스 중에는 경과를 말하지 않는다** (절대 규칙 85).
              `getElapsedSec()` 이 **라이브의 초**라 회차의 경과로 그리면 거짓이고,
              남은 시간도 말하지 않는다 — 끝나는 시각은 **콘솔의 값이지 시청자에게 한
              약속이 아니다**(`docs/67` §2.2). 배지의 근거는 첫 프레임이다
              (`view.state === 'playing'`) — `started` 로 그리면 **검은 화면 위에
              「LIVE」**가 뜬다(ONAIR 가 값을 치른 그 자리 · `docs/57` §1.3).
            */}
            {view.live !== null ? (
              view.state === 'playing' && (
                <span className="clock" data-testid="live-badge">
                  <span className="badge badge--live">{t('watch.liveBadge')}</span>{' '}
                  <span data-i18n="data">{view.live.title}</span>
                </span>
              )
            ) : view.adBreak === null ? (
              <PlaybackClock durationSec={item.durationSec} getElapsedSec={getElapsedSec} />
            ) : (
              <AdCountdown
                durationSec={view.adBreak.totalSec}
                getElapsedSec={getAdBreakElapsedSec}
              />
            )}
            <MuteButton />
          </div>
        </>
      )}

      {/*
        `switching` 에는 **아무것도 그리지 않는다** (`docs/06-frontend-spec.md` 「상태 표시」).
        전환 중 인디케이터는 그것 자체가 끊김으로 보인다 — 무갭으로 만들어 놓고
        인디케이터로 끊김을 연출하는 셈이 된다.
      */}
      {/*
        ★ **잡은 프레임 위에는 띄우지 않는다** (2026-09-06). 직전 채널의 그림 위에
        「불러오는 중」을 얹으면 `docs/06` 「상태 표시」가 막아 둔 그 일이 된다 —
        **무갭으로 만들어 놓고 인디케이터로 끊김을 연출하는 셈**이다.
        걷힌 뒤(상한 2초 또는 첫 프레임)에는 지금까지처럼 이것이 그 창을 갖는다.
      */}
      {view.started && view.state === 'loading' && !frozen && (
        <div className="notice">{t('watch.loading')}</div>
      )}

      {/*
        `data-focus-scope` — 이 스크림은 `inset: 0` 에 거의 불투명해서 **컨트롤 바를 덮는다.**
        층으로 표시하지 않으면 리모컨 포커스가 스크림 뒤 버튼에 서고, 그 상태의 Enter 는
        **화면에 보이지 않는 일을 한다** (절대 규칙 32).

        `data-focus-back` 은 붙이지 않는다 — 여기 있는 것은 닫기가 아니라 **재시도**다.
        이 층에서 뒤로 키는 아무 일도 하지 않고 브라우저에게 넘어간다.
      */}
      {view.fatalError !== null && (
        <div className="notice notice--error" data-focus-scope>
          <p className="notice__message">{view.fatalError.message}</p>
          {/*
            ★★ **이유를 함께 그린다** (2026-09-06). `code` 하나만 남기면 **화면에도
            로그에도 왜 죽었는지가 없다** — 어댑터는 `hls 치명적 오류: networkError /
            fragLoadError` 를 `cause` 에 담아 보내는데 **아무도 그것을 못 봤다.**
            무대 신고를 쫓는 데 그날 세 시간이 들었고, 그 값이 화면에 있었으면 곧바로
            그 줄로 갔다. 시청자에게는 뜻 없는 문자열이지만 **무대에 선 사람에게는
            그것이 전부다.**
          */}
          <p className="notice__code">{failureCode(view.fatalError)}</p>
          <button type="button" className="gate__button" onClick={retry}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {/*
        「전체 상품」 시트 (v11). **오류 스크림 뒤에 그린다** — 층 우선순위는 DOM 순서를
        따르고(절대 규칙 32) 이 시트는 z-31, 스크림은 z-10 이라 **둘이 일치한다.**
        (v11 에서는 이 시트가 z-20 이라 앞에 두면 어긋났다. 2026-08-10 에 `.sheet` 로
        옮기며 z 가 올라갔지만 **자리는 그대로 두는 것이 맞다.**)

        `fatalError` 가 있으면 아예 그리지 않는다. 그 화면에서 할 일은 재시도 하나이고,
        목록을 남겨 두면 골라도 같은 오류로 돌아온다.

        **시트 셋(`.sheet`)이 같은 층이고, 동시에 열리지 않는 것이 계약이다.** 전에는
        `.stage--mini .controls { display: none }` 이 물리적으로 보장했고 지금은 스크림이
        보장한다 — 열린 시트가 컨트롤 바를 덮어 다른 시트를 열 수 없고, 리모컨 포커스는
        층에 갇힌다. 그래서 상호 배제 코드를 따로 두지 않는다(지금 도달 불가라 죽은 코드다).
      */}
      {productSheetOpen && view.fatalError === null && (
        <ProductListSheet
          isGeneral={isGeneral}
          resume={view.resume !== null && view.resume.positionSec > 0 ? view.resume : null}
          // v21. **정책이 `null` 이면 배지가 없다** — 그 채널의 시트는 한 픽셀도 안 바뀐다.
          adPolicy={view.adPolicy}
          rows={productRows}
          content={{
            mode: contentMode,
            channelId,
            rows: contentRows,
            currentProductId: item?.productId ?? null,
            catalogFailed,
          }}
          onPick={pickProduct}
          onClose={() => {
            setProductSheetOpen(false);
          }}
        />
      )}

      {/*
        ★ **판매처에서 돌아온 사람에게는 「이어서 보기」다** (v21/F13).

        설치된 앱에서는 외부로 나가는 모든 방법이 앱을 그 자리에서 대체하므로
        (`left-for-checkout.ts`) 돌아오면 이 게이트를 다시 만난다. **없앨 수 없다** —
        소리 있는 재생은 제스처 한 번을 요구한다. 없앨 수 없는 것과 **「처음 보기」처럼
        보이는 것은 다르다**: 채널과 지점은 이미 살아 있다.

        **한 번만 읽는다**(`useState` 의 지연 초기화). 매 렌더에서 읽으면 표시를 지우는
        일이 렌더 중에 일어나고, StrictMode 의 두 번째 렌더에서 이미 사라진다.
      */}
      {/*
        ★★ **전환에서는 원리적으로 안 뜬다** (2026-09-06 · `showsStartGate`).
        조건이 「아직 안 틀었다」가 아니라 **「제스처가 필요하다」**이고, 그 판정의
        중심인 `buffersUnlocked()` 는 모듈 스코프라 **재마운트를 건너 살아남는다.**
      */}
      {showsStartGate({
        started: view.started,
        unlocked: buffersUnlocked(),
        autoStart,
        gestureRequired: view.gestureRequired,
      }) && (
        <StartGate
          onStart={start}
          /*
            **채널을 아직 못 받았으면 그리지 않는다** — 빈 편성표를 여는 버튼은
            「눌렀는데 아무것도 없다」다.
          */
          onOpenGuide={
            epgRows.length > 0
              ? () => {
                  // **여는 순간 다시 받는다** (v27/TG15 · `ChannelGate` 의 ★★)
                  onEpgOpen();
                  setChannelSheetOpen(true);
                }
              : null
          }
          resuming={resuming}
        />
      )}

      {/*
        설정 시트. 흡수된 모바일 웹의 홈이 여기로 왔다 (`docs/14` B7 — "홈 내용은 시청
        화면 위의 설정 시트로").

        **버튼을 진입 화면 위에도 띄운다.** 알림을 켜는 것은 방송을 보기 전에 하는 일이고,
        더 실질적으로는 **폰이 Tier 2 의 대상이 되려면 등록·구독이 트리거보다 먼저여야
        한다.** 컨트롤 바에 넣지 않은 이유는 그 셋(구매·정보·건너뛰기)이 사양이기 때문이다
        (`docs/06`) — 설정은 시청 동작이 아니다.
      */}
      <button
        type="button"
        className="stage__settings"
        aria-label={t('watch.settings')}
        data-testid="settings"
        onClick={() => {
          setSettingsOpen((open) => !open);
        }}
      >
        {t('watch.settings')}
      </button>

      {/*
        채널 시트. **재생 위에 뜬다** (절대 규칙 14) — 여는 것은 전환이 아니고, 같은
        채널을 다시 고르면 아무 일도 일어나지 않는다.
      */}
      {channelSheetOpen && (
        <ChannelSheet
          inLive={view.live !== null}
          /*
           * ★ **사실만 넘긴다** (v27, `docs/55` D14).
           *
           * v25 는 여기서 `applyCurrentRow` 를 불렀는데, 그것은 **시트를 여는 순간 한 번**
           * 계산되는 값이라 **흐르지 않는다** — 시각축에서는 그 행이 매 초 왼쪽으로 가야
           * 한다. 그래서 그 호출은 `EpgScreen` 안으로 옮겼고(**함수는 그대로 쓴다**),
           * 여기는 시계를 갖지 않는다.
           *
           * **여기에 시계를 심으면 안 되는 이유**도 그대로다 — 매 초 이 화면이 다시
           * 그려지면 다섯 행 백 개 상자가 초당 한 번 재조정되고, **화면은 멀쩡하며
           * 폰에서만 더워진다.**
           */
          rows={epgRows}
          now={{
            channelId,
            /*
             * ★ **함수 하나를 넘긴다.** 값을 넘기면 **시트가 열려 있는 동안 얼어붙는다** —
             * 이 화면은 다시 그려질 이유가 없다(시계는 `EpgScreen` 이 갖는다). 프리롤
             * 중에 시트를 열면 `productId` 는 `null` 로, `inAdBreak` 은 `true` 로 굳어
             * **앵커가 아예 안 잡히고 재생 중인 행이 흐르지 않는다**
             * (`useReceiver` 의 `getPlayhead` 주석 · 2026-09-04).
             */
            getPlayhead,
            // v34 — 지금 채널의 라이브 상자를 리시버의 새 창으로(주소는 이미 뗐다 · 규칙 68·87)
            getLiveSlots,
            // v39 — 재생 중인 회차가 편성표에 없으면(라이브러리 꼬리 · 비로그인) 그 행을 이 목록으로 세운다
            items: view.items,
          }}
          /*
           * ★ **「보는 중」은 재생 중일 때만이다** (2026-09-06 무대 신고).
           *
           * 여기서 `channelId` 를 그냥 넘기고 있었는데, **이 시트는 진입 게이트에서도
           * 열린다**(바로 아래 `if (!view.started)` 가 그 증거다) — 그래서 아무것도
           * 재생되고 있지 않은데 그 채널에 「보는 중」이 붙었다.
           */
          currentChannelId={watchingChannelId({ channelId, started: view.started })}
          currentProductId={view.item?.productId ?? null}
          favorites={favorites}
          onToggleFavorite={onToggleFavorite}
          signedIn={auth.token !== null}
          onPick={(nextChannelId, productId, isEntryCard) => {
            /*
             * ★ **게이트에서 고르면 바로 시작한다** (v25).
             *
             * **이 클릭이 제스처다.** 게이트는 재생 허가가 아직 없는 자리이고, 채널이
             * 바뀌는 경우 실제 `start()` 는 새 화면의 `useEffect` 안이라 **제스처 밖**이다 —
             * v12 가 채널 전환에서 만난 그 모양이다. 그래서 **여기서** `unlockBuffers()` 로
             * 허가를 심는다(v19 가 상품 화면의 `[방송 보기]` 를 위해 만든 그 함수).
             *
             * **첫 장이면 회차를 가리키지 않는다** — 규칙 68 이 여기서도 같다.
             * 「지금」 카드로 들어가면 이어보기가 살아 있어야 한다.
             */
            if (!view.started) {
              unlockBuffers();
              const at = isEntryCard ? null : productId;
              if (nextChannelId === channelId) {
                // 같은 채널 — 리시버가 이미 서 있다. **재마운트하지 않는다**(게이트만 깜빡인다).
                setChannelSheetOpen(false);
                startFrom(at);
              } else {
                /*
                 * 다른 채널 — `switchTo` 가 `autoStart` 를 켠다. 허가는 위에서 심었다.
                 * v29. **시트를 닫지 않는다** — 새 화면이 열린 채로 마운트하고, 그 채널이
                 * 실제로 나올 때 걷힌다. 여기서 닫으면 그 사이가 빈다.
                 */
                onSwitchChannel(nextChannelId, at ?? undefined, true);
              }
              return;
            }
            const pick = resolveEpgPick({
              currentChannelId: channelId,
              currentProductId: view.item?.productId ?? null,
              pickedChannelId: nextChannelId,
              pickedProductId: productId,
              isEntryCard,
              /*
               * ★ v33-b. **바이패스 중이면 「같은 것」이 아니다** (`epg.ts` 의 ★★).
               * 화면이 아는 사실을 넘긴다 — 순수 함수가 DOM 을 볼 수는 없다.
               */
              inLive: view.live !== null,
            });
            /*
             * v29. **닫는 것은 전환이 아닌 갈래뿐이다** (2026-09-06). 전환이면 시트를
             * 그대로 두고 `fromEpg` 로 넘긴다 — `ChannelGate` 가 빈 렌더에서 이어 그리고
             * 새 화면이 열린 채로 마운트한다.
             */
            if (pick.kind !== 'switch') setChannelSheetOpen(false);
            if (pick.kind === 'same') return;
            /*
             * v33-b. **한 채널 안에서 두 축을 오간다** (`docs/67` §9.4). 둘 다 리시버의
             * 상태 전환이라 채널 전환도 재마운트도 없다 — `pickProduct` 와 같은 자리다.
             */
            if (pick.kind === 'live') {
              void joinLive();
              return;
            }
            if (pick.kind === 'resumeRecorded') {
              void resumeRecorded();
              return;
            }
            if (pick.kind === 'select') {
              // **같은 채널이면 목록 선택 그 자체다** (절대 규칙 69). `pickProduct` 를
              // 그대로 부르면 `resolvePick`(D5) · 규칙 33 의 셋 · 규칙 35 의 포커스가
              // 전부 공짜로 따라온다. 여기서 `onSwitchChannel` 을 부르면 가드에 걸려
              // **아무 일도 일어나지 않는다.**
              void pickProduct(pick.productId);
              return;
            }
            // **전환 전에** 보고한다 (`docs/38` §6.6). 전환은 이 화면을 통째로
            // 언마운트하므로 그 뒤에는 보낼 주체가 없다.
            // **`await` 하지 않는다** — 보고가 채널 전환을 늦추면 규칙 27 이 재려는
            // 전환 성능이 그만큼 거짓이 된다.
            reportEvent('channel_switch');
            if (pick.startFromProductId !== undefined) {
              /*
               * ★ **갈 채널의 `channelId`·`isGeneral` 을 실어야 한다** (v25).
               * 그대로 보내면 서버가 소속 검사에서 **202 로 조용히 버리고**, 고른 회차의
               * 지점이 안 남아 **다음 진입이 옛 회차로 돌아간다.** 새 화면에서 보내려 하면
               * `channelKind` 가 `playlistLoaded` 전까지 `null` 이라 **또 조용히 버려진다** —
               * 그래서 떠나는 화면이 낸다.
               */
              const target = epgRows.find((r) => r.channel.channelId === nextChannelId);
              reportEvent('select', {
                channelId: nextChannelId,
                /*
                 * ★ **v28 — `kind` 만 보면 ONAIR 채널로 진행 보고가 나간다**
                 * (`docs/57` §9.1 의 10번). 서버가 202 로 버리지만 **보내는 쪽도 알고
                 * 있어야** 그 조용함이 자물쇠 하나에 매이지 않는다(규칙 70 의 방침).
                 */
                tracksProgress: target !== undefined && target.channel.delivery === 'receiver',
                productId: pick.startFromProductId,
                elapsedSec: 0,
              });
            }
            // v29. **세 번째 인자가 「편성표에서 골랐다」**이고, 그것이 시트를 전환 너머로
            // 살려 둔다 (2026-09-06).
            onSwitchChannel(pick.channelId, pick.startFromProductId, true);
          }}
          onClose={() => {
            setChannelSheetOpen(false);
          }}
        />
      )}

      {settingsOpen && (
        <SettingsSheet
          init={tv2m.init}
          subscribed={tv2m.pushSubscribed}
          linkedToAccount={tv2m.linkedToAccount}
          runtime={runtime}
          auth={auth}
          ready={tv2m.ready}
          needsIosInstall={tv2m.needsIosInstall}
          lastContext={lastContext}
          // v21. **`null` 이면 광고 줄 자체가 없다** (절대 규칙 56) — 홈쇼핑 채널에서
          // 없는 것을 끌 수 있게 보이면 그것이 곧 「홈쇼핑에도 광고가 있다」는 신호다.
          adPolicy={view.adPolicy}
          // v23. **`null` 이면 상품 추천 줄 자체가 없다** (절대 규칙 66) — 홈쇼핑 채널에서
          // 없는 것을 끌 수 있게 보이면 그것이 곧 「홈쇼핑에도 상품이 있다」는 신호다.
          offerPolicy={view.offerPolicy}
          onOpenLast={onOpenLast}
          onOpenChannels={
            epgRows.length > 1
              ? () => {
                  // 설정 시트를 닫고 채널 시트를 연다. 둘을 겹쳐 띄우지 않는다.
                  setSettingsOpen(false);
                  setChannelSheetOpen(true);
                }
              : null
          }
          onEnablePush={tv2m.enablePush}
          onClose={() => {
            setSettingsOpen(false);
          }}
        />
      )}

      {config.debug && (
        <DebugPanel
          view={view}
          telemetry={telemetry}
          snapshot={snapshot}
          clientType={clientType}
          handoff={handoff}
          resumeCheck={resumeCheck}
        />
      )}
    </div>
  );
}

/**
 * 트리거 단위 식별자 (`docs/08` §2 의 `campaignId`).
 *
 * **한 곳에서 만든다.** 요청 본문과 시트가 그리는 컨텍스트와 이벤트가 같은 값을 써야
 * `Tv2mEvent` 와 `MatchSession` 이 이어진다 — 세 곳에 각각 적으면 어긋나도 화면에는
 * 드러나지 않고 분석에서만 갈린다.
 *
 * **채널을 인자로 받는다 (v9).** 설정값을 읽으면 채널을 바꾼 뒤에도 옛 채널의 값이
 * 실리고, 디바운스 키가 `(hashedIp, productId, contentId, receiverDeviceId)` 라
 * (`docs/21` §5.1) **다른 채널의 같은 상품이 앞의 세션을 되읽는다.** 증상은 「채널을
 * 바꿨는데 앞 채널의 매칭 결과가 뜬다」 하나다.
 */
function campaignIdOf(channelId: string, productId: string): string {
  return `${channelId}:${productId}`;
}

/**
 * 오류 스크림의 코드 줄 (2026-09-06). **`cause` 를 잃지 않는다.**
 *
 * 어댑터가 `new Error('hls 치명적 오류: <type> / <details>')` 를 `cause` 로 실어 보내는데,
 * `code` 만 그리면 그 문장이 어디에도 남지 않는다.
 */
function failureCode(error: { code: string; cause?: unknown }): string {
  const cause = error.cause;
  const detail = cause instanceof Error ? cause.message : typeof cause === 'string' ? cause : '';
  return detail === '' ? error.code : `${error.code} · ${detail}`;
}
